import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  LinearProgress,
  Toolbar,
  Tooltip,
  Autocomplete,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popover,
  Stack,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  DirectionsCar as DirectionsCarIcon,
  Assignment as AssignmentIcon,
  LocalGasStation,
  Speed,
  PersonAdd,
  FilterList,
  Refresh,
  Print,
  Email,
  Download,
  Upload,
  BugReport,
  Add as AddDefectIcon,
  Delete as DeleteDefectIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowUpward,
  ArrowDownward,
  FilterListOff,
  EventBusy as EventBusyIcon,
  CalendarMonth as CalendarMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CloudUpload,
  PhotoCamera,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  ArrowDropDown,
  Description as DescriptionIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { DateInputField } from '../components/DateInputField';
import { TableFilterCell } from '../components/TableFilterCell';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { reservationApi } from '../services/api';
import { companyLocationApi } from '../services/api';
import { customerAccountApi } from '../services/api';
import { driverApi } from '../services/api';
import { rentalTypeApi } from '../services/api';
import { vehicleApi } from '../services/api';
import { defectApi } from '../services/api';
import { documentApi } from '../services/api';
import { invoiceApi } from '../services/api';
import { serviceTemplateApi } from '../services/api';
import {
  Reservation,
  ReservationCustomer,
  ReservationStatus,
  ReservationCreateRequest,
  ReservationUpdateRequest,
} from '../types/reservation';
import {
  CustomerAccountRecord,
  CustomerAccountSaveRequest,
  CustomerType,
  createEmptyCustomerAccountForm,
} from '../types/customerAccount';
import {
  createEmptyDriverForm,
  DriverLinkedCustomerSummary,
  DriverRecord,
  DriverSaveRequest,
} from '../types/driver';
import { Vehicle } from '../types/vehicle';
import { RentalTypeRecord } from '../types/rentalType';
import { Defect, DefectType, DefectSeverity, DefectCreateRequest } from '../types/defect';
import { ReservationServiceItem, ReservationServiceItemCreateRequest, SERVICE_TYPES, ServiceType } from '../types/serviceItem';
import { ServiceTemplate } from '../types/serviceTemplate';
import { ReservationHistoryEvent } from '../types/reservationHistory';
import { InvoiceRecord } from '../types/invoice';
import { DocumentExtractionResult } from '../types/document';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReservationStatusChip } from '../components/ReservationStatusChip';
import { formatDate, formatDateTime, formatCurrency, CURRENCY_SYMBOL, getWhatsAppLink } from '../utils/formatters';
import { safeFilePart } from '../utils/downloadFile';
import { compressImageForUpload } from '../utils/imageCompression';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../i18n/languages';
import { defectSeverityLabel, defectTypeLabel, enumLabel, serviceTypeLabel, statusLabel } from '../i18n/translate';
import { ManagedDocument } from '../types/document';
import { CompanyLocation, formatCompanyLocationLabel } from '../types/companyLocation';
import { useDomainSettingsQuery } from '../settings/useDomainSettingsQuery';
import { EmailSendDialog } from '../components/email/EmailSendDialog';
import { EmailLogPanel } from '../components/email/EmailLogPanel';
import { WhatsAppSendDialog } from '../components/whatsapp/WhatsAppSendDialog';
import { CustomerFormFields } from '../components/CustomerFormFields';
import { InvoiceDetailsPanel } from '../components/invoices/InvoiceDetailsPanel';
import { getReservationBillToInfo } from '../utils/billing';

dayjs.extend(customParseFormat);

type ReservationCustomerOption = {
  id: number;
  type: CustomerType;
  displayName: string;
  legalName?: string | null;
  contactPersonName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  taxId?: string | null;
  registrationNumber?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  notes?: string | null;
  active: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  totalReservations: number;
};

type ReservationCustomerSaveRequest = CustomerAccountSaveRequest;
const createEmptyReservationCustomerForm = createEmptyCustomerAccountForm;

const SHOW_RENTAL_DEFECTS_SECTION = false;
const DEFAULT_PICKUP_TIME = '09:00';
const DEFAULT_RETURN_TIME = '17:00';
const DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp';
const RENTAL_LIST_STATUS_FILTER_ALL = 'ALL';
const DEFAULT_RENTAL_SORT_CONFIG = {
  key: 'reservationNumber',
  direction: 'desc' as const,
};
const FUEL_LEVEL_OPTIONS = ['EMPTY', 'QUARTER', 'HALF', 'THREE_QUARTERS', 'FULL'] as const;
type RentalListStatusFilter = typeof RENTAL_LIST_STATUS_FILTER_ALL | ReservationStatus;
const RENTAL_DETAIL_UNIFORM_FIELDS_SX = {
  '& .MuiFormControl-root .MuiInputBase-root': {
    minHeight: 52,
    alignItems: 'center',
  },
  '& .MuiFormControl-root .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
  },
  '& .rental-detail-notes .MuiInputBase-inputMultiline': {
    paddingTop: '8.5px',
    paddingBottom: '8.5px',
    overflow: 'hidden',
    resize: 'none',
  },
};
const RENTAL_TIME_FIELD_SX = {
  '& .MuiInputBase-input': {
    paddingRight: '4px',
    fontSize: '0.95rem',
  },
  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    width: '14px',
    height: '14px',
    margin: 0,
    position: 'relative',
    right: '-2px',
  },
};
const CLICKABLE_BOOKING_FIELD_SX = {
  cursor: 'pointer',
  '& .MuiInputBase-root': {
    cursor: 'pointer',
  },
  '& input': {
    cursor: 'pointer',
  },
};

function getBookingPickerMonthDays(month: Dayjs): Array<Dayjs | null> {
  const firstDay = month.startOf('month');
  const leadingEmptyDays = (firstDay.day() + 6) % 7;
  const calendarDays: Array<Dayjs | null> = Array.from({ length: leadingEmptyDays }, () => null);

  for (let day = 1; day <= month.daysInMonth(); day += 1) {
    calendarDays.push(month.date(day));
  }

  while (calendarDays.length % 7 !== 0) {
    calendarDays.push(null);
  }

  return calendarDays;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ServiceRow {
  id?: number;
  templateId?: string;
  allowPriceChange?: boolean;
  serviceType: string;
  serviceName: string;
  description?: string;
  unitPrice: number;
  quantity: number;
  serviceLocation?: string;
  isNew?: boolean;
  isEditing?: boolean;
  isPendingRental?: boolean;
}

interface RentalTimelinePrefill {
  vehicle: Vehicle;
  startDate: string;
  endDate: string;
}

interface RentalManagementLocationState {
  rentalPrefill?: RentalTimelinePrefill;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rental-tabpanel-${index}`}
      aria-labelledby={`rental-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

function getRentalTimeFieldValue(time: Dayjs | null, fallbackTime: string) {
  return time ? time.format('HH:mm') : fallbackTime;
}

function parseRentalTimeFieldValue(value: string, fallbackTime: string) {
  const parsedTime = dayjs(value || fallbackTime, 'HH:mm', true);
  return parsedTime.isValid() ? parsedTime : dayjs(fallbackTime, 'HH:mm', true);
}

function formatReservationHistoryTimestamp(value?: string | null) {
  if (!value) {
    return '';
  }
  return formatDateTime(value);
}

function formatDocumentFileSize(bytes?: number) {
  if (!bytes || bytes <= 0) {
    return '0 KB';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const OTHER_LOCATION_VALUE = '__other__';

function getSelectableLocationById(
  locations: CompanyLocation[],
  locationId?: number | null
) {
  if (!locationId) {
    return null;
  }

  return locations.find((location) => location.id === locationId) ?? null;
}

function findLocationIdBySnapshot(
  locations: CompanyLocation[],
  snapshot?: string | null
) {
  const normalizedSnapshot = snapshot?.trim();
  if (!normalizedSnapshot) {
    return null;
  }

  const matchedLocation = locations.find(
    (location) => formatCompanyLocationLabel(location) === normalizedSnapshot
  );
  return matchedLocation?.id ?? null;
}

function createEmptyRentalDriverForm(): DriverSaveRequest {
  return {
    ...createEmptyDriverForm(),
    emailNotifications: true,
    smsNotifications: false,
    status: 'ACTIVE',
  };
}

function toDriverSelection(
  driver?: Partial<DriverRecord> | Reservation['driver'] | null
): DriverRecord | null {
  if (!driver?.id) {
    return null;
  }

  const fullName =
    'fullName' in driver && typeof driver.fullName === 'string' && driver.fullName.trim()
      ? driver.fullName.trim()
      : `${driver.firstName || ''} ${driver.lastName || ''}`.trim();

  return {
    id: driver.id,
    firstName: driver.firstName || '',
    lastName: driver.lastName || '',
    fullName,
    email: driver.email || '',
    phoneNumber: driver.phoneNumber || '',
    status: ('status' in driver && driver.status) || 'ACTIVE',
    dateOfBirth: ('dateOfBirth' in driver && driver.dateOfBirth) || '',
    personalIdNumber: ('personalIdNumber' in driver && driver.personalIdNumber) || '',
    driverLicenseNumber: ('driverLicenseNumber' in driver && driver.driverLicenseNumber) || '',
    licenseIssueDate: ('licenseIssueDate' in driver && driver.licenseIssueDate) || '',
    licenseExpiryDate: ('licenseExpiryDate' in driver && driver.licenseExpiryDate) || '',
    licenseCategories: ('licenseCategories' in driver && driver.licenseCategories) || '',
    notes: ('notes' in driver && driver.notes) || '',
    preferredLanguage: normalizeLanguage(
      (('preferredLanguage' in driver && driver.preferredLanguage) || 'en') as string
    ),
    emailNotifications:
      ('emailNotifications' in driver && driver.emailNotifications) ?? true,
    smsNotifications:
      ('smsNotifications' in driver && driver.smsNotifications) ?? false,
    createdAt: ('createdAt' in driver && driver.createdAt) || '',
    updatedAt: ('updatedAt' in driver && driver.updatedAt) || '',
    totalReservations:
      ('totalReservations' in driver && typeof driver.totalReservations === 'number')
        ? driver.totalReservations
        : 0,
    totalRentals:
      ('totalRentals' in driver && typeof driver.totalRentals === 'number')
        ? driver.totalRentals
        : 0,
    linkedCustomer:
      ('linkedCustomer' in driver && driver.linkedCustomer) || null,
    linkedCustomers:
      ('linkedCustomers' in driver && Array.isArray(driver.linkedCustomers))
        ? driver.linkedCustomers
        : [],
  };
}

function toDriverSaveRequest(driver?: Partial<DriverRecord> | null): DriverSaveRequest {
  return {
    ...createEmptyRentalDriverForm(),
    firstName: driver?.firstName || '',
    lastName: driver?.lastName || '',
    email: driver?.email || '',
    phoneNumber: driver?.phoneNumber || '',
    dateOfBirth: driver?.dateOfBirth || '',
    personalIdNumber: driver?.personalIdNumber || '',
    driverLicenseNumber: driver?.driverLicenseNumber || '',
    licenseIssueDate: driver?.licenseIssueDate || '',
    licenseExpiryDate: driver?.licenseExpiryDate || '',
    licenseCategories: driver?.licenseCategories || '',
    notes: driver?.notes || '',
    preferredLanguage: normalizeLanguage(driver?.preferredLanguage || 'en'),
    emailNotifications: driver?.emailNotifications ?? true,
    smsNotifications: driver?.smsNotifications ?? false,
    status: driver?.status || 'ACTIVE',
    linkedCustomerId: driver?.linkedCustomer?.id ?? null,
  };
}

function toSelfBillingCustomerAccountRequest(
  driver: DriverSaveRequest
): CustomerAccountSaveRequest {
  const displayName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim();

  return {
    type: 'PERSON',
    displayName,
    contactPersonName: displayName,
    email: driver.email || '',
    phoneNumber: driver.phoneNumber,
    taxId: driver.personalIdNumber || '',
    registrationNumber: driver.driverLicenseNumber || '',
    notes: driver.notes || '',
    active: true,
    driver: {
      ...driver,
    },
  };
}

function toCustomerOption(customerAccount: CustomerAccountRecord): ReservationCustomerOption {
  return {
    id: customerAccount.id,
    type: customerAccount.type,
    displayName: customerAccount.displayName,
    legalName: customerAccount.legalName || '',
    contactPersonName: customerAccount.contactPersonName || '',
    email: customerAccount.email || '',
    phoneNumber: customerAccount.phoneNumber || '',
    taxId: customerAccount.taxId || '',
    registrationNumber: customerAccount.registrationNumber || '',
    streetAddress: customerAccount.streetAddress || '',
    city: customerAccount.city || '',
    state: customerAccount.state || '',
    postalCode: customerAccount.postalCode || '',
    country: customerAccount.country || '',
    notes: customerAccount.notes || '',
    active: customerAccount.active,
    createdAt: customerAccount.createdAt || '',
    updatedAt: customerAccount.updatedAt || '',
    totalReservations: 0,
  };
}

function toCustomerOptionFromReservation(
  customerAccount: ReservationCustomer
): ReservationCustomerOption {
  return {
    id: customerAccount.id,
    type: customerAccount.type,
    displayName: customerAccount.displayName,
    legalName: customerAccount.legalName || '',
    contactPersonName: customerAccount.contactPersonName || '',
    email: customerAccount.email || '',
    phoneNumber: customerAccount.phoneNumber || '',
    taxId: customerAccount.taxId || '',
    registrationNumber: customerAccount.registrationNumber || '',
    streetAddress: customerAccount.streetAddress || '',
    city: customerAccount.city || '',
    state: customerAccount.state || '',
    postalCode: customerAccount.postalCode || '',
    country: customerAccount.country || '',
    notes: customerAccount.notes || '',
    active: customerAccount.active ?? true,
    createdAt: '',
    updatedAt: '',
    totalReservations: 0,
  };
}

function getPreferredLinkedCustomer(
  driver?: Partial<DriverRecord> | null
): DriverLinkedCustomerSummary | null {
  if (!driver) {
    return null;
  }

  if (driver.linkedCustomer?.id) {
    return driver.linkedCustomer;
  }

  const linkedCustomers = Array.isArray(driver.linkedCustomers) ? driver.linkedCustomers : [];
  if (linkedCustomers.length === 0) {
    return null;
  }

  return linkedCustomers[0];
}

function toCustomerOptionFromLinkedCustomer(
  linkedCustomer: DriverLinkedCustomerSummary
): ReservationCustomerOption {
  return {
    id: linkedCustomer.id,
    type: linkedCustomer.type,
    displayName: linkedCustomer.displayName,
    legalName: linkedCustomer.legalName || '',
    contactPersonName: linkedCustomer.contactPersonName || '',
    email: '',
    phoneNumber: '',
    taxId: '',
    registrationNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    notes: '',
    active: linkedCustomer.active !== false,
    createdAt: '',
    updatedAt: '',
    totalReservations: 0,
  };
}

export const RentalManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const BOOKING_PICKER_WEEKDAYS = [
    t('common.weekdaysShort.mon', 'Mo'),
    t('common.weekdaysShort.tue', 'Tu'),
    t('common.weekdaysShort.wed', 'We'),
    t('common.weekdaysShort.thu', 'Th'),
    t('common.weekdaysShort.fri', 'Fr'),
    t('common.weekdaysShort.sat', 'Sa'),
    t('common.weekdaysShort.sun', 'Su'),
  ];
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = user?.company?.id;
  const settingsQuery = useDomainSettingsQuery(companyId);
  const multipleLocationsEnabled = Boolean(settingsQuery.data?.multipleLocations);
  const locationState = location.state as RentalManagementLocationState | null;
  const appliedRentalPrefillRef = useRef<string | null>(null);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState(false);
  const [selectedDocumentFiles, setSelectedDocumentFiles] = useState<File[]>([]);
  const [isDraggingDocuments, setIsDraggingDocuments] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  
  // Column filters and sorting
  const [columnFilters, setColumnFilters] = useState({
    reservationNumber: '',
    customer: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleLicensePlate: '',
    startDate: '',
    endDate: '',
    status: '',
    total: '',
  });

  // Rental list filter mode. 'active' bypasses the date window entirely and loads every
  // IN_PROGRESS reservation regardless of age, so a rental nobody checked out doesn't
  // quietly disappear. 'upcoming'/'all' are windowed (1 month back, 2 months forward by
  // default) instead of the company's entire history, since a company running for years
  // could otherwise pull thousands of long-settled rentals on every page load - the
  // popup filter lets staff switch modes and widen that window when needed.
  const [rentalListFilter, setRentalListFilter] = useState<'active' | 'upcoming' | 'all'>('all');
  const rentalListDefaultRange = useMemo(
    () => ({
      start: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      end: dayjs().add(2, 'month').format('YYYY-MM-DD'),
    }),
    []
  );
  const [rentalListCustomRange, setRentalListCustomRange] = useState<{ start: string; end: string } | null>(null);
  const rentalListRange = rentalListCustomRange ?? rentalListDefaultRange;
  const [rentalFilterDialogOpen, setRentalFilterDialogOpen] = useState(false);
  const [rentalFilterDraftMode, setRentalFilterDraftMode] = useState<'active' | 'upcoming' | 'all'>('all');
  const [rentalListStatusFilter, setRentalListStatusFilter] = useState<RentalListStatusFilter>(RENTAL_LIST_STATUS_FILTER_ALL);
  const [rentalFilterDraftStatus, setRentalFilterDraftStatus] = useState<RentalListStatusFilter>(RENTAL_LIST_STATUS_FILTER_ALL);
  const [rentalFilterDraftStart, setRentalFilterDraftStart] = useState('');
  const [rentalFilterDraftEnd, setRentalFilterDraftEnd] = useState('');
  const rentalListPopupFilterActive =
    rentalListFilter !== 'all' ||
    rentalListCustomRange !== null ||
    rentalListStatusFilter !== RENTAL_LIST_STATUS_FILTER_ALL;
  const rentalListDateRangeEnabled = rentalFilterDraftMode !== 'active';
  const canApplyRentalListFilter =
    rentalFilterDraftMode === 'active' || Boolean(rentalFilterDraftStart && rentalFilterDraftEnd);

  const openRentalFilterDialog = () => {
    setRentalFilterDraftMode(rentalListFilter);
    setRentalFilterDraftStatus(rentalListStatusFilter);
    setRentalFilterDraftStart(rentalListRange.start);
    setRentalFilterDraftEnd(rentalListRange.end);
    setRentalFilterDialogOpen(true);
  };
  const handleApplyRentalListFilter = () => {
    if (rentalFilterDraftMode !== 'active' && (!rentalFilterDraftStart || !rentalFilterDraftEnd)) return;
    setRentalListFilter(rentalFilterDraftMode);
    setRentalListStatusFilter(rentalFilterDraftStatus);
    if (rentalFilterDraftMode === 'active') {
      setRentalListCustomRange(null);
    } else {
      const nextRange = {
        start: rentalFilterDraftStart || rentalListDefaultRange.start,
        end: rentalFilterDraftEnd || rentalListDefaultRange.end,
      };
      const usesCustomRange =
        nextRange.start !== rentalListDefaultRange.start || nextRange.end !== rentalListDefaultRange.end;
      setRentalListCustomRange(usesCustomRange ? nextRange : null);
    }
    setPage(0);
    setRentalFilterDialogOpen(false);
  };
  const handleResetRentalListFilter = () => {
    setRentalListFilter('all');
    setRentalFilterDraftMode('all');
    setRentalListStatusFilter(RENTAL_LIST_STATUS_FILTER_ALL);
    setRentalFilterDraftStatus(RENTAL_LIST_STATUS_FILTER_ALL);
    setRentalFilterDraftStart(rentalListDefaultRange.start);
    setRentalFilterDraftEnd(rentalListDefaultRange.end);
    setRentalListCustomRange(null);
    setPage(0);
    setRentalFilterDialogOpen(false);
  };

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>(
    () => ({ ...DEFAULT_RENTAL_SORT_CONFIG })
  );

  // Walk-in rental state
  const [selectedDriver, setSelectedDriver] = useState<DriverRecord | null>(null);
  const [selectedRentalType, setSelectedRentalType] = useState<RentalTypeRecord | null>(null);
  const [selectedCustomerAccount, setSelectedCustomerAccount] =
    useState<ReservationCustomerOption | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [startTime, setStartTime] = useState<Dayjs | null>(dayjs().hour(9).minute(0));
  const [endTime, setEndTime] = useState<Dayjs | null>(dayjs().hour(17).minute(0));
  const [returnDateUnknown, setReturnDateUnknown] = useState(false);
  const [bookingPickerAnchorEl, setBookingPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [bookingPickerMonth, setBookingPickerMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [bookingPickerDraftStart, setBookingPickerDraftStart] = useState<Dayjs | null>(startDate);
  const [bookingPickerDraftEnd, setBookingPickerDraftEnd] = useState<Dayjs | null>(endDate);
  const bookingPickerOpen = Boolean(bookingPickerAnchorEl);
  const rentalDateRangeText = startDate
    ? `${formatDate(startDate)} - ${
        returnDateUnknown
          ? t('rental.returnDateTbd')
          : endDate
            ? formatDate(endDate)
            : t('fields.endDate')
      }`
    : '';
  const [rentalFormData, setRentalFormData] = useState({
    insuranceIncluded: false,
    notes: '',
    status: ReservationStatus.CONFIRMED, // Default status for new rentals
  });
  const [selectedPickupLocationId, setSelectedPickupLocationId] = useState<number | null>(null);
  const [selectedReturnLocationId, setSelectedReturnLocationId] = useState<number | null>(null);
  const [isPickupLocationOther, setIsPickupLocationOther] = useState(false);
  const [isReturnLocationOther, setIsReturnLocationOther] = useState(false);
  
  // Expandable sections state
  const [showPickupInspection, setShowPickupInspection] = useState(false);
  const [showDefects, setShowDefects] = useState(false);

  // Inspection state
  const [inspectionType, setInspectionType] = useState<'pickup' | 'return'>('pickup');
  const [inspectionData, setInspectionData] = useState({
    mileage: '', // pickup mileage
    fuelLevel: 'FULL', // pickup fuel level
      returnMileage: '', // return mileage
      returnFuelLevel: 'FULL', // return fuel level
  });

  // Defects state
  const [defects, setDefects] = useState<Partial<DefectCreateRequest>[]>([]);
  const [showDefectDialog, setShowDefectDialog] = useState(false);
  const [editingDefectIndex, setEditingDefectIndex] = useState<number | null>(null);

  // Mileage warning dialog state
  const [showMileageWarningDialog, setShowMileageWarningDialog] = useState(false);
  const [mileageWarningMessage, setMileageWarningMessage] = useState('');
  const [pendingMileageAction, setPendingMileageAction] = useState<(() => void) | null>(null);
  const [mileageWarningType, setMileageWarningType] = useState<'pickup' | 'return' | null>(null);
  const [defectFormData, setDefectFormData] = useState<Partial<DefectCreateRequest>>({
    type: DefectType.OTHER,
    severity: DefectSeverity.LOW,
    title: '',
    description: '',
    location: '',
    component: '',
    safetyImpact: false,
    operationalImpact: false,
    customerImpact: false,
    vehicleOutOfService: false,
  });

  // Service Items state
  const [serviceItems, setServiceItems] = useState<ReservationServiceItem[]>([]);
  const [serviceRows, setServiceRows] = useState<ServiceRow[]>([]);

  // Driver selection
  const [showNewDriverDialog, setShowNewDriverDialog] = useState(false);
  const [showNewBillingPartyDialog, setShowNewBillingPartyDialog] = useState(false);
  const [newBillingPartyData, setNewBillingPartyData] = useState<ReservationCustomerSaveRequest>(
    createEmptyReservationCustomerForm()
  );

  // View/edit an already-registered driver from the rental form
  const [showDriverDetailsDialog, setShowDriverDetailsDialog] = useState(false);
  const [driverDetailsTarget, setDriverDetailsTarget] = useState<DriverRecord | null>(null);
  const [driverDetailsData, setDriverDetailsData] = useState<DriverSaveRequest>(
    createEmptyRentalDriverForm()
  );

  const handleOpenDriverDetails = (driver: DriverRecord) => {
    setDriverDetailsTarget(driver);
    setDriverDetailsData(toDriverSaveRequest(driver));
    setShowDriverDetailsDialog(true);
  };

  // The driver embedded in a reservation/the picker only carries a few fields client-side;
  // fetch the full record once the dialog opens so edits are based on current, complete data.
  const { data: driverDetailsFull } = useQuery(
    ['driverDetails', driverDetailsTarget?.id],
    () => driverApi.getById(driverDetailsTarget!.id.toString()),
    { enabled: showDriverDetailsDialog && !!driverDetailsTarget }
  );

  useEffect(() => {
    if (!driverDetailsFull) return;
    setDriverDetailsData(toDriverSaveRequest(driverDetailsFull));
  }, [driverDetailsFull]);

  const [newCustomerData, setNewCustomerData] = useState<DriverSaveRequest>(
    createEmptyRentalDriverForm()
  );

  // New driver license AI import
  type LicenseUploadSide = 'front' | 'back';
  type LicenseProcessingState = 'idle' | 'uploading' | 'extracting' | 'completed' | 'failed' | 'skipped';
  const newCustomerFrontLicenseInputRef = useRef<HTMLInputElement>(null);
  const newCustomerBackLicenseInputRef = useRef<HTMLInputElement>(null);
  const [newCustomerFrontLicenseFile, setNewCustomerFrontLicenseFile] = useState<File | null>(null);
  const [newCustomerBackLicenseFile, setNewCustomerBackLicenseFile] = useState<File | null>(null);
  const [newCustomerFrontLicensePreviewUrl, setNewCustomerFrontLicensePreviewUrl] = useState('');
  const [newCustomerBackLicensePreviewUrl, setNewCustomerBackLicensePreviewUrl] = useState('');
  const [newCustomerLicenseDraggingSide, setNewCustomerLicenseDraggingSide] = useState<LicenseUploadSide | null>(null);
  const [newCustomerLicenseUploadProgress, setNewCustomerLicenseUploadProgress] = useState(0);
  const [newCustomerLicenseProcessingState, setNewCustomerLicenseProcessingState] = useState<LicenseProcessingState>('idle');
  const [newCustomerLicenseStatusMessage, setNewCustomerLicenseStatusMessage] = useState<string | null>(null);
  const [newCustomerLicenseExtraction, setNewCustomerLicenseExtraction] = useState<DocumentExtractionResult | null>(null);
  const isNewCustomerLicenseBusy = newCustomerLicenseProcessingState === 'uploading' || newCustomerLicenseProcessingState === 'extracting';

  const resetNewCustomerLicenseUpload = () => {
    setNewCustomerFrontLicenseFile(null);
    setNewCustomerBackLicenseFile(null);
    setNewCustomerLicenseDraggingSide(null);
    setNewCustomerLicenseUploadProgress(0);
    setNewCustomerLicenseProcessingState('idle');
    setNewCustomerLicenseStatusMessage(null);
    setNewCustomerLicenseExtraction(null);
    if (newCustomerFrontLicenseInputRef.current) newCustomerFrontLicenseInputRef.current.value = '';
    if (newCustomerBackLicenseInputRef.current) newCustomerBackLicenseInputRef.current.value = '';
  };

  useEffect(() => {
    if (!newCustomerFrontLicenseFile) {
      setNewCustomerFrontLicensePreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(newCustomerFrontLicenseFile);
    setNewCustomerFrontLicensePreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [newCustomerFrontLicenseFile]);

  useEffect(() => {
    if (!newCustomerBackLicenseFile) {
      setNewCustomerBackLicensePreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(newCustomerBackLicenseFile);
    setNewCustomerBackLicensePreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [newCustomerBackLicenseFile]);

  const uploadNewCustomerLicenseMutation = useMutation(documentApi.uploadAndExtract, {
    onSuccess: (response) => {
      setNewCustomerLicenseUploadProgress(100);
      setNewCustomerLicenseExtraction(response.extraction || null);
      setNewCustomerLicenseStatusMessage(response.message || null);

      const nextState: LicenseProcessingState =
        response.processingStatus === 'COMPLETED'
          ? 'completed'
          : response.processingStatus === 'SKIPPED'
            ? 'skipped'
            : 'failed';
      setNewCustomerLicenseProcessingState(nextState);

      const extractedLicense = response.extraction?.driverLicense;
      if (extractedLicense) {
        setNewCustomerData((current) => ({
          ...current,
          firstName: extractedLicense.firstName || current.firstName,
          lastName: extractedLicense.lastName || current.lastName,
          personalIdNumber: extractedLicense.personalIdNumber || current.personalIdNumber,
          dateOfBirth: extractedLicense.dateOfBirth || current.dateOfBirth,
          driverLicenseNumber: extractedLicense.licenseNumber || current.driverLicenseNumber,
          licenseIssueDate: extractedLicense.dateOfIssue || current.licenseIssueDate,
          licenseExpiryDate: extractedLicense.expiryDate || current.licenseExpiryDate,
          licenseCategories: extractedLicense.licenseCategories || current.licenseCategories,
        }));
      }
    },
    onError: (err: any) => {
      setNewCustomerLicenseExtraction(null);
      setNewCustomerLicenseProcessingState('failed');
      setNewCustomerLicenseStatusMessage(
        err.response?.data?.error ||
        err.response?.data?.message ||
        t('booking.document.extractionFailed', 'We could not process the uploaded document. You can still complete the form manually.')
      );
    },
  });

  const handleNewCustomerLicenseFileSelected = async (side: LicenseUploadSide, file: File | null) => {
    const processedFile = file ? await compressImageForUpload(file) : null;
    if (side === 'front') {
      setNewCustomerFrontLicenseFile(processedFile);
    } else {
      setNewCustomerBackLicenseFile(processedFile);
    }
    setNewCustomerLicenseUploadProgress(0);
    setNewCustomerLicenseProcessingState('idle');
    setNewCustomerLicenseStatusMessage(null);
    setNewCustomerLicenseExtraction(null);
  };

  const handleNewCustomerLicenseDrop = (side: LicenseUploadSide, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setNewCustomerLicenseDraggingSide(null);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    handleNewCustomerLicenseFileSelected(side, file);
  };

  const handleRemoveNewCustomerLicenseFile = (side: LicenseUploadSide) => {
    if (side === 'front') {
      setNewCustomerFrontLicenseFile(null);
      if (newCustomerFrontLicenseInputRef.current) newCustomerFrontLicenseInputRef.current.value = '';
    } else {
      setNewCustomerBackLicenseFile(null);
      if (newCustomerBackLicenseInputRef.current) newCustomerBackLicenseInputRef.current.value = '';
    }
    setNewCustomerLicenseUploadProgress(0);
    setNewCustomerLicenseProcessingState('idle');
    setNewCustomerLicenseStatusMessage(null);
    setNewCustomerLicenseExtraction(null);
  };

  const handleUploadNewCustomerLicense = () => {
    if (!newCustomerFrontLicenseFile) {
      setNewCustomerLicenseStatusMessage(
        t('booking.document.frontRequired', 'Please upload the front of the driver license first.')
      );
      setNewCustomerLicenseProcessingState('failed');
      return;
    }

    setNewCustomerLicenseUploadProgress(0);
    setNewCustomerLicenseProcessingState('uploading');
    setNewCustomerLicenseStatusMessage(t('booking.document.uploading', 'Uploading the document securely...'));

    uploadNewCustomerLicenseMutation.mutate({
      documentType: 'DRIVER_LICENSE',
      frontFile: newCustomerFrontLicenseFile,
      backFile: newCustomerBackLicenseFile,
      onUploadProgress: (progressPercent: number) => {
        setNewCustomerLicenseUploadProgress(progressPercent);
        if (progressPercent >= 100) {
          setNewCustomerLicenseProcessingState('extracting');
          setNewCustomerLicenseStatusMessage(t('booking.document.extracting', 'AI is reading the document and extracting the fields...'));
        } else {
          setNewCustomerLicenseProcessingState('uploading');
        }
      },
    });
  };

  // Fetch reservations - scoped by rentalListFilter (see state above). 'active' hits a
  // status-only endpoint with no date bound; 'upcoming'/'all' hit the windowed range
  // endpoint and are further narrowed to CONFIRMED for 'upcoming' below.
  const { data: rentalListRawReservations = [], isLoading: loadingReservations } = useQuery(
    ['companyReservations', companyId, rentalListFilter, rentalListRange.start, rentalListRange.end],
    () =>
      rentalListFilter === 'active'
        ? reservationApi.getCompanyReservationsByStatus(companyId!, ReservationStatus.IN_PROGRESS)
        : reservationApi.getCompanyReservationsInRange(companyId!, rentalListRange.start, rentalListRange.end),
    {
      enabled: !!companyId,
      keepPreviousData: true,
    }
  );

  const reservations = useMemo(() => {
    let nextReservations = rentalListRawReservations;
    if (rentalListFilter === 'upcoming') {
      nextReservations = nextReservations.filter((r) => r.status === ReservationStatus.CONFIRMED);
    }
    if (rentalListStatusFilter !== RENTAL_LIST_STATUS_FILTER_ALL) {
      nextReservations = nextReservations.filter((r) => r.status === rentalListStatusFilter);
    }
    return nextReservations;
  }, [rentalListRawReservations, rentalListFilter, rentalListStatusFilter]);

  // Fetch customers
  const { data: customers = [], isLoading: searchingCustomers } = useQuery<DriverRecord[]>(
    ['drivers'],
    () => driverApi.getAll(),
    { enabled: isCreating || editMode }
  );

  const { data: customerAccounts = [], isLoading: loadingCustomerAccounts } =
    useQuery<ReservationCustomerOption[]>(
    ['customerAccounts', 'active'],
    () => customerAccountApi.getAll(undefined, true).then((rows) => rows.map(toCustomerOption)),
    { enabled: isCreating || editMode }
  );

  const { data: rentalTypes = [] } = useQuery<RentalTypeRecord[]>(
    ['rentalTypes', 'active'],
    () => rentalTypeApi.getActive()
  );
  const isCompanyKind = selectedRentalType?.kind === 'COMPANY';
  const defaultPersonRentalType = () =>
    rentalTypes.find((rentalType) => rentalType.isSystemDefault && rentalType.kind === 'PERSON') ?? null;
  // Most companies only keep one rental type active (usually Person), so there's nothing
  // to actually choose - fall back to it directly instead of forcing a Person default that
  // may not even be active.
  const resolveDefaultRentalType = () =>
    rentalTypes.length === 1 ? rentalTypes[0] : defaultPersonRentalType();
  const resolveRentalTypeForReservation = (reservation: Reservation): RentalTypeRecord | null => {
    const rentalTypeId = reservation.rentalTypeId;
    if (rentalTypeId != null) {
      const matched = rentalTypes.find((rentalType) => rentalType.id === rentalTypeId);
      if (matched) {
        return matched;
      }
    }
    // Legacy reservation with no rentalTypeId yet (pre-migration data) - fall back to
    // inferring from the boolean the way this form always used to work.
    const fallbackKind = reservation.customerSameAsDriver === false ? 'COMPANY' : 'PERSON';
    return rentalTypes.find((rentalType) => rentalType.isSystemDefault && rentalType.kind === fallbackKind) ?? null;
  };

  const resolveCustomerForDriver = (
    driver: DriverRecord | null
  ): ReservationCustomerOption | null => {
    const linkedCustomer = getPreferredLinkedCustomer(driver);
    if (!linkedCustomer || linkedCustomer.active === false) {
      return null;
    }

    return (
      customerAccounts.find((customerAccount) => customerAccount.id === linkedCustomer.id) ||
      toCustomerOptionFromLinkedCustomer(linkedCustomer)
    );
  };

  // Fetch vehicles
  const { data: vehicles = [], isLoading: loadingVehicles } = useQuery(
    'companyVehicles',
    () => vehicleApi.getAll(),
    { enabled: isCreating || editMode }
  );
  const { data: companyLocations = [] } = useQuery(
    ['companyLocations', companyId, 'active-rental'],
    () => companyLocationApi.getAll(true),
    {
      enabled: !!companyId && multipleLocationsEnabled,
    }
  );

  const vehicleAvailabilityStartDate = startDate?.format('YYYY-MM-DD') || '';
  const vehicleAvailabilityEndDate = returnDateUnknown ? '' : (endDate?.format('YYYY-MM-DD') || '');
  const hasVehicleAvailabilityWindow = Boolean(
    vehicleAvailabilityStartDate && (returnDateUnknown || vehicleAvailabilityEndDate)
  );

  const { data: vehicleAvailability = {}, isLoading: loadingVehicleAvailability } = useQuery(
    [
      'vehicleAvailability',
      vehicles.map((vehicle) => vehicle.id).join(','),
      vehicleAvailabilityStartDate,
      vehicleAvailabilityEndDate,
      returnDateUnknown,
    ],
    async () => {
      const entries = await Promise.all(
        vehicles.map(async (vehicle) => [
          vehicle.id,
          await reservationApi.checkAvailability(
            vehicle.id,
            vehicleAvailabilityStartDate,
            returnDateUnknown ? undefined : vehicleAvailabilityEndDate
          ),
        ] as const)
      );
      return entries.reduce<Record<number, boolean>>((availabilityByVehicle, [vehicleId, isAvailable]) => {
        availabilityByVehicle[vehicleId] = isAvailable;
        return availabilityByVehicle;
      }, {});
    },
    {
      enabled: (isCreating || editMode) && vehicles.length > 0 && hasVehicleAvailabilityWindow,
      staleTime: 30 * 1000,
    }
  );
  const pickupLocationOptions = useMemo(
    () => companyLocations.filter((location) => location.active && location.allowPickup),
    [companyLocations]
  );
  const returnLocationOptions = useMemo(
    () => companyLocations.filter((location) => location.active && location.allowReturn),
    [companyLocations]
  );

  const getPreferredPickupLocationId = (vehicle?: Vehicle | null) => {
    const vehicleLocation = getSelectableLocationById(pickupLocationOptions, vehicle?.companyLocationId);
    if (vehicleLocation) {
      return vehicleLocation.id;
    }

    return (
      pickupLocationOptions.find((location) => location.defaultPickup)?.id ??
      pickupLocationOptions[0]?.id ??
      null
    );
  };

  const getPreferredReturnLocationId = (pickupLocationId?: number | null) => {
    const sameLocation = getSelectableLocationById(returnLocationOptions, pickupLocationId);
    if (sameLocation) {
      return sameLocation.id;
    }

    return (
      returnLocationOptions.find((location) => location.defaultReturn)?.id ??
      returnLocationOptions[0]?.id ??
      null
    );
  };

  const selectedPickupLocation = getSelectableLocationById(
    pickupLocationOptions,
    selectedPickupLocationId
  );
  const selectedReturnLocation = getSelectableLocationById(
    returnLocationOptions,
    selectedReturnLocationId
  );
  const hasPickupLocationSelection = isPickupLocationOther || Boolean(selectedPickupLocation);
  const hasReturnLocationSelection = isReturnLocationOther || Boolean(selectedReturnLocation);

  useEffect(() => {
    if (!multipleLocationsEnabled) {
      if (selectedPickupLocationId !== null) {
        setSelectedPickupLocationId(null);
      }
      if (selectedReturnLocationId !== null) {
        setSelectedReturnLocationId(null);
      }
      if (isPickupLocationOther) {
        setIsPickupLocationOther(false);
      }
      if (isReturnLocationOther) {
        setIsReturnLocationOther(false);
      }
      return;
    }

    const preferredPickupLocationId =
      getSelectableLocationById(pickupLocationOptions, selectedVehicle?.companyLocationId)?.id ??
      pickupLocationOptions.find((location) => location.defaultPickup)?.id ??
      pickupLocationOptions[0]?.id ??
      null;
    const resolvePreferredReturnLocationId = (pickupLocationId?: number | null) =>
      getSelectableLocationById(returnLocationOptions, pickupLocationId)?.id ??
      returnLocationOptions.find((location) => location.defaultReturn)?.id ??
      returnLocationOptions[0]?.id ??
      null;

    if (selectedReservation) {
      const matchedPickupLocationId =
        selectedReservation.pickupLocationId ??
        findLocationIdBySnapshot(pickupLocationOptions, selectedReservation.pickupLocation);
      const reservationPickupLocationId = matchedPickupLocationId ?? preferredPickupLocationId;
      const matchedReturnLocationId =
        selectedReservation.returnLocationId ??
        findLocationIdBySnapshot(returnLocationOptions, selectedReservation.returnLocation);
      const reservationReturnLocationId =
        matchedReturnLocationId ?? resolvePreferredReturnLocationId(reservationPickupLocationId);

      if (!selectedPickupLocationId && !isPickupLocationOther) {
        if (!matchedPickupLocationId && selectedReservation.pickupLocation?.trim()) {
          setIsPickupLocationOther(true);
        } else if (reservationPickupLocationId) {
          setSelectedPickupLocationId(reservationPickupLocationId);
        }
      }

      if (!selectedReturnLocationId && !isReturnLocationOther) {
        if (!matchedReturnLocationId && selectedReservation.returnLocation?.trim()) {
          setIsReturnLocationOther(true);
        } else if (reservationReturnLocationId) {
          setSelectedReturnLocationId(reservationReturnLocationId);
        }
      }

      return;
    }

    if (!isCreating && !editMode) {
      return;
    }

    if (!selectedPickupLocationId) {
      const nextPickupLocationId = preferredPickupLocationId;
      if (nextPickupLocationId) {
        setSelectedPickupLocationId(nextPickupLocationId);
      }
    }

    if (!selectedReturnLocationId) {
      const nextReturnLocationId = resolvePreferredReturnLocationId(
        selectedPickupLocationId ?? preferredPickupLocationId
      );
      if (nextReturnLocationId) {
        setSelectedReturnLocationId(nextReturnLocationId);
      }
    }
  }, [
    editMode,
    multipleLocationsEnabled,
    pickupLocationOptions,
    returnLocationOptions,
    selectedPickupLocationId,
    selectedReservation,
    selectedReturnLocationId,
    selectedVehicle,
    isCreating,
    isPickupLocationOther,
    isReturnLocationOther,
  ]);

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const getReservationNumberSortValue = (reservationNumber?: string | null) => {
    const raw = (reservationNumber || '').trim();
    const numericValue = Number.parseInt(raw, 10);
    return Number.isNaN(numericValue) ? null : numericValue;
  };

  // Helper: get sum of service items - matches Services section, excludes tax/fees
  // Prefer summing serviceItems when available (most accurate); fall back to subtotal/totalAmount
  const getReservationSubtotal = (r: Reservation) => {
    const fromServiceItems = r.serviceItems?.length
      ? r.serviceItems.reduce((sum, item) => sum + ((item.unitPrice || 0) * (item.quantity || 1)), 0)
      : null;
    return fromServiceItems ?? r.subtotal ?? r.totalAmount;
  };

  // Filter reservations based on column filters
  const filteredReservations = reservations.filter((reservation) => {
    const startDateStr = reservation.startDate ? formatDate(reservation.startDate).toLowerCase() : '';
    const endDateStr = reservation.endDate ? formatDate(reservation.endDate).toLowerCase() : '';
    const driverFullName = reservation.driver
      ? `${reservation.driver.firstName || ''} ${reservation.driver.lastName || ''}`.trim()
      : (reservation.guestDriverName || '');

    return (
      (!columnFilters.reservationNumber || (reservation.reservationNumber || '').toLowerCase().includes(columnFilters.reservationNumber.toLowerCase())) &&
      (!columnFilters.customer || driverFullName.toLowerCase().includes(columnFilters.customer.toLowerCase())) &&
      (!columnFilters.vehicleMake || (reservation.vehicle?.make || '').toLowerCase().includes(columnFilters.vehicleMake.toLowerCase())) &&
      (!columnFilters.vehicleModel || (reservation.vehicle?.model || '').toLowerCase().includes(columnFilters.vehicleModel.toLowerCase())) &&
      (!columnFilters.vehicleLicensePlate || (reservation.vehicle?.licensePlate || '').toLowerCase().includes(columnFilters.vehicleLicensePlate.toLowerCase())) &&
      (!columnFilters.startDate || startDateStr.includes(columnFilters.startDate.toLowerCase())) &&
      (!columnFilters.endDate || endDateStr.includes(columnFilters.endDate.toLowerCase())) &&
      (!columnFilters.status || (reservation.status || '').toLowerCase().includes(columnFilters.status.toLowerCase())) &&
      (!columnFilters.total || (getReservationSubtotal(reservation)?.toString() || '').includes(columnFilters.total))
    );
  });

  // Sort filtered reservations
  const sortedReservations = [...filteredReservations].sort((a, b) => {
    const aIsPending = a.status === ReservationStatus.PENDING;
    const bIsPending = b.status === ReservationStatus.PENDING;
    if (aIsPending !== bIsPending) {
      return aIsPending ? -1 : 1;
    }

    let aValue: any;
    let bValue: any;

    switch (sortConfig.key) {
      case 'reservationNumber': {
        const aNumber = getReservationNumberSortValue(a.reservationNumber);
        const bNumber = getReservationNumberSortValue(b.reservationNumber);
        if (aNumber !== null && bNumber !== null) {
          aValue = aNumber;
          bValue = bNumber;
        } else {
          aValue = (a.reservationNumber || '').toLowerCase();
          bValue = (b.reservationNumber || '').toLowerCase();
        }
        break;
      }
      case 'customer':
        aValue = (a.driver ? `${a.driver.firstName || ''} ${a.driver.lastName || ''}`.trim() : (a.guestDriverName || '')).toLowerCase();
        bValue = (b.driver ? `${b.driver.firstName || ''} ${b.driver.lastName || ''}`.trim() : (b.guestDriverName || '')).toLowerCase();
        break;
      case 'vehicleMake':
        aValue = (a.vehicle?.make || '').toLowerCase();
        bValue = (b.vehicle?.make || '').toLowerCase();
        break;
      case 'vehicleModel':
        aValue = (a.vehicle?.model || '').toLowerCase();
        bValue = (b.vehicle?.model || '').toLowerCase();
        break;
      case 'vehicleLicensePlate':
        aValue = (a.vehicle?.licensePlate || '').toLowerCase();
        bValue = (b.vehicle?.licensePlate || '').toLowerCase();
        break;
      case 'startDate':
        aValue = a.startDate || '';
        bValue = b.startDate || '';
        break;
      case 'endDate':
        aValue = a.endDate || '';
        bValue = b.endDate || '';
        break;
      case 'status':
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
        break;
      case 'total':
        aValue = getReservationSubtotal(a) || 0;
        bValue = getReservationSubtotal(b) || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedReservations = sortedReservations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Mutations
  const createReservationMutation = useMutation(
    (data: ReservationCreateRequest) => reservationApi.create(data),
    {
      onSuccess: async (newReservation: Reservation) => {
        queryClient.invalidateQueries(['companyReservations', companyId]);
        
        // Use captured service rows from ref (state might be cleared by resetForm)
        const rowsToSave = serviceRowsToSaveRef.current;
        
        // Save all service rows immediately after reservation is created
        if (newReservation.id && rowsToSave.length > 0) {
          try {
            // First, fetch the service items that were auto-created by the backend
            const existingServiceItems = await reservationApi.getServiceItems(newReservation.id.toString());
            
            // Find the RENTAL service row from frontend
            const rentalServiceRow = rowsToSave.find(row => row.serviceType === 'RENTAL');
            
            // Find the RENTAL service item that was auto-created by backend
            const existingRentalService = existingServiceItems.find(item => item.serviceType === 'RENTAL');
            
            // Always update the RENTAL service to ensure it matches frontend values and total is correct
            if (rentalServiceRow && existingRentalService) {
              // Always update to ensure values match (even if they appear the same, this ensures total is recalculated)
                await reservationApi.updateServiceItem(existingRentalService.id!.toString(), {
                  serviceTemplateId: rentalServiceRow.templateId ? parseInt(rentalServiceRow.templateId) : undefined,
                  serviceType: rentalServiceRow.serviceType,
                  serviceName: rentalServiceRow.serviceName,
                  description: rentalServiceRow.description,
                  unitPrice: rentalServiceRow.unitPrice,
                  quantity: rentalServiceRow.quantity,
                  serviceLocation: rentalServiceRow.serviceLocation,
                });
            } else if (rentalServiceRow && !existingRentalService) {
              // RENTAL service doesn't exist (shouldn't happen, but handle it)
              await reservationApi.addServiceItem(newReservation.id.toString(), {
                serviceTemplateId: rentalServiceRow.templateId ? parseInt(rentalServiceRow.templateId) : undefined,
                serviceType: rentalServiceRow.serviceType,
                serviceName: rentalServiceRow.serviceName,
                description: rentalServiceRow.description,
                unitPrice: rentalServiceRow.unitPrice,
                quantity: rentalServiceRow.quantity,
                serviceLocation: rentalServiceRow.serviceLocation,
              });
            }
            
            // Save all other new service rows (excluding RENTAL which we just handled)
            const newServiceRows = rowsToSave.filter(row => row.isNew && row.serviceType !== 'RENTAL');
            for (const row of newServiceRows) {
              try {
                await reservationApi.addServiceItem(newReservation.id.toString(), {
                  serviceTemplateId: row.templateId ? parseInt(row.templateId) : undefined,
                  serviceType: row.serviceType,
                  serviceName: row.serviceName,
                  description: row.description,
                  unitPrice: row.unitPrice,
                  quantity: row.quantity,
                  serviceLocation: row.serviceLocation,
                });
              } catch (error: any) {
                console.error('Error adding service item:', error);
                toast.error(t('rental.messages.failedAddService', { service: row.serviceName }));
              }
            }
            
            // Invalidate queries to refresh the total
            queryClient.invalidateQueries(['reservationServiceItems', newReservation.id.toString()]);
            queryClient.invalidateQueries(['companyReservations', companyId]);
          } catch (error: any) {
            console.error('Error saving service items:', error);
            toast.error(t('rental.messages.createdPartial'));
          }
        }
        
        // Clear the ref after use
        serviceRowsToSaveRef.current = [];
        
        // If pickup mileage or other inspection data was set, update the reservation immediately
        const currentInspectionData = inspectionData; // Capture before resetForm
        if (currentInspectionData.mileage && currentInspectionData.mileage.trim() !== '') {
          const mileageValue = parseInt(currentInspectionData.mileage);
          if (!isNaN(mileageValue)) {
            try {
              const updateData: ReservationUpdateRequest = {
                pickupMileage: mileageValue,
              };
              if (currentInspectionData.fuelLevel && currentInspectionData.fuelLevel.trim() !== '') {
                updateData.fuelLevelPickup = currentInspectionData.fuelLevel;
              }
              await reservationApi.update(newReservation.id.toString(), updateData);
              
              // Invalidate and refetch to update the table immediately
              await queryClient.invalidateQueries(['companyReservations', companyId]);
              await queryClient.refetchQueries(['companyReservations', companyId]);
            } catch (error: any) {
              console.error('💾 [CREATE SUCCESS] Failed to update reservation with pickup data:', error);
              // Don't show error to user, just log it
            }
          }
        }

        const refreshedReservation = await reservationApi
          .getById(newReservation.id.toString())
          .catch(() => newReservation);
        toast.success(t('rental.messages.created'));
        loadReservation(refreshedReservation);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.createFailed'));
      },
    }
  );

  const updateReservationMutation = useMutation(
    ({ id, data }: { id: string; data: ReservationUpdateRequest }) => {
      return reservationApi.update(id, data);
    },
    {
      onSuccess: async (updatedReservation) => {
        
        // Update selectedReservation state if this is the one being edited
        if (selectedReservation?.id === updatedReservation.id) {
          setSelectedReservation(updatedReservation);
        }

        queryClient.invalidateQueries(['reservationHistory', updatedReservation.id.toString()]);
        
        // Invalidate and refetch with correct query key
        await queryClient.invalidateQueries(['companyReservations', companyId]);
        await queryClient.refetchQueries(['companyReservations', companyId]);
        
        // Use captured service rows from ref (state might be cleared)
        const rowsToSave = serviceRowsToSaveRef.current;
        
        // Save/update all service rows that are new or modified
        if (selectedReservation?.id && rowsToSave.length > 0) {
          try {
            // Fetch existing service items
            const existingServiceItems = await reservationApi.getServiceItems(selectedReservation.id.toString());
            const normalizeNumber = (value: number | string | null | undefined) => Number(value ?? 0);
            const normalizeText = (value: string | null | undefined) => value ?? '';
            const buildServiceItemData = (row: typeof rowsToSave[number]): ReservationServiceItemCreateRequest => ({
              serviceTemplateId: row.templateId ? parseInt(row.templateId) : undefined,
              serviceType: row.serviceType,
              serviceName: row.serviceName,
              description: row.description,
              unitPrice: row.unitPrice,
              quantity: row.quantity,
              serviceLocation: row.serviceLocation,
            });
            
            // Process each service row and persist any value that differs from the server copy.
            for (const row of rowsToSave) {
              const serviceItemData = buildServiceItemData(row);

              if (row.isNew) {
                // Save new service items
                try {
                  await reservationApi.addServiceItem(selectedReservation.id.toString(), serviceItemData);
                } catch (error: any) {
                  console.error('Error adding service item:', error);
                  toast.error(t('rental.messages.failedAddService', { service: row.serviceName }));
                }
              } else if (row.id) {
                const existingItem = existingServiceItems.find(item => item.id === row.id);
                if (existingItem) {
                  const needsUpdate = 
                    (existingItem.serviceTemplateId?.toString() ?? '') !== (row.templateId ?? '') ||
                    existingItem.serviceType !== row.serviceType ||
                    existingItem.serviceName !== row.serviceName ||
                    normalizeText(existingItem.description) !== normalizeText(row.description) ||
                    normalizeNumber(existingItem.unitPrice) !== normalizeNumber(row.unitPrice) ||
                    normalizeNumber(existingItem.quantity) !== normalizeNumber(row.quantity) ||
                    normalizeText(existingItem.serviceLocation) !== normalizeText(row.serviceLocation);
                  
                  if (needsUpdate) {
                    try {
                      await reservationApi.updateServiceItem(row.id.toString(), serviceItemData);
                    } catch (error: any) {
                      console.error('Error updating service item:', error);
                      toast.error(
                        row.serviceType === 'RENTAL'
                          ? t('rental.messages.failedUpdateRentalService')
                          : t('rental.messages.failedUpdateService', { service: row.serviceName })
                      );
                    }
                  }
                }
              }
            }

            // Mark rows that were just created as no longer new so a later Save
            // (e.g. triggered by a status change) doesn't re-POST them as duplicates.
            setServiceRows(prev => prev.map(row => (row.isNew ? { ...row, isNew: false } : row)));

            // Invalidate queries to refresh the total
            queryClient.invalidateQueries(['reservationServiceItems', selectedReservation.id.toString()]);

            // Explicitly refetch the reservation to get updated totalAmount and pickupMileage
            await queryClient.invalidateQueries(['companyReservations', companyId]);
            await queryClient.refetchQueries(['companyReservations', companyId]);
          } catch (error: any) {
            console.error('Error saving service items:', error);
            toast.error(t('rental.messages.updatedPartial'));
          }
        } else {
          // Even if no service items to save, refetch to ensure we have latest data
          await queryClient.invalidateQueries(['companyReservations', companyId]);
          await queryClient.refetchQueries(['companyReservations', companyId]);
        }
        
        // Clear the ref after use
        serviceRowsToSaveRef.current = [];
        
        toast.success(t('rental.messages.updated'));
        setIsDirty(false);
      },
      onError: (error: any) => {
        console.error('❌ [MUTATION ERROR] Failed to update reservation:', error);
        console.error('❌ [MUTATION ERROR] Error response:', error.response?.data);
        toast.error(error.response?.data?.error || t('rental.messages.updateFailed'));
      },
    }
  );

  const startReservationMutation = useMutation(
    (data: { pickupMileage?: number; fuelLevel: string }) =>
      reservationApi.start(selectedReservation!.id.toString(), data),
    {
      onSuccess: (updatedReservation) => {
        setSelectedReservation(updatedReservation);
        queryClient.invalidateQueries(['companyReservations', companyId]);
        queryClient.invalidateQueries(['reservationHistory', updatedReservation.id.toString()]);
        queryClient.invalidateQueries('reservations');
        toast.success(t('rental.messages.pickupCompleted'));
        setInspectionData({
          mileage: '',
          fuelLevel: 'FULL',
          returnMileage: '',
          returnFuelLevel: 'FULL',
        });
        setDefects([]);
        setDetailTab(0);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.pickupFailed'));
      },
    }
  );

  const completeReservationMutation = useMutation(
    (data: { returnMileage?: number; fuelLevel: string; additionalFees?: number }) =>
      reservationApi.complete(selectedReservation!.id.toString(), data),
    {
      onSuccess: (updatedReservation) => {
        setSelectedReservation(updatedReservation);
        queryClient.invalidateQueries(['companyReservations', companyId]);
        queryClient.invalidateQueries(['reservationHistory', updatedReservation.id.toString()]);
        queryClient.invalidateQueries('reservations');
        toast.success(t('rental.messages.returnCompleted'));
        setInspectionData({
          mileage: '',
          fuelLevel: 'FULL',
          returnMileage: '',
          returnFuelLevel: 'FULL',
        });
        setDefects([]);
        setDetailTab(0);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.returnFailed'));
      },
    }
  );

  const createDefectMutation = useMutation(
    (data: DefectCreateRequest) => defectApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('defects');
        toast.success(t('rental.messages.defectReported'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.defectFailed'));
      },
    }
  );

  const createDriverMutation = useMutation(
    (driverData: DriverSaveRequest) =>
      customerAccountApi.create(toSelfBillingCustomerAccountRequest(driverData)),
    {
      onSuccess: (createdCustomerAccount) => {
        const newCustomer = toDriverSelection(createdCustomerAccount.driverDetails);
        if (!newCustomer) {
          toast.error(
            t(
              'rental.messages.driverRegisterFailed',
              'The driver was created, but we could not load it back into the rental form.'
            )
          );
          queryClient.invalidateQueries(['drivers']);
          queryClient.invalidateQueries(['customerAccounts']);
          return;
        }

        toast.success(t('rental.messages.driverRegistered', 'Driver registered.'));
        setSelectedDriver(newCustomer);
        setSelectedCustomerAccount(
          isCompanyKind ? toCustomerOption(createdCustomerAccount) : null
        );
        setShowNewDriverDialog(false);
        setNewCustomerData(createEmptyRentalDriverForm());

        const licenseFiles = [newCustomerFrontLicenseFile, newCustomerBackLicenseFile].filter(
          (file): file is File => Boolean(file)
        );
        if (licenseFiles.length > 0) {
          driverApi
            .uploadDocuments({
              driverId: newCustomer.id.toString(),
              documentType: 'DRIVER_LICENSE',
              files: licenseFiles,
            })
            .catch(() => {
              toast.error(
                t(
                  'booking.document.extractionFailed',
                  'We could not process the uploaded document. You can still complete the form manually.'
                )
              );
            });
        }
        resetNewCustomerLicenseUpload();

        queryClient.invalidateQueries(['drivers']);
        queryClient.invalidateQueries(['customerAccounts']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.driverRegisterFailed', 'Failed to register driver.'));
      },
    }
  );

  const updateDriverDetailsMutation = useMutation(
    (data: DriverSaveRequest) => driverApi.update(driverDetailsTarget!.id.toString(), data),
    {
      onSuccess: (updatedDriver) => {
        toast.success(t('rental.messages.driverUpdated', 'Driver updated.'));
        queryClient.invalidateQueries(['drivers']);
        if (selectedDriver?.id === updatedDriver.id) {
          setSelectedDriver(updatedDriver);
        }
        if (selectedReservation?.driver?.id === updatedDriver.id) {
          setSelectedReservation({ ...selectedReservation, driver: updatedDriver });
        }
        setShowDriverDetailsDialog(false);
        setDriverDetailsTarget(null);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.driverUpdateFailed', 'Failed to update driver.'));
      },
    }
  );

  const createCustomerAccountMutation = useMutation(
    (data: ReservationCustomerSaveRequest) => customerAccountApi.create(data),
    {
      onSuccess: (createdCustomerAccount) => {
        toast.success(t('customers.messages.created', 'Customer created.'));
        queryClient.invalidateQueries(['customerAccounts']);
        setSelectedCustomerAccount(toCustomerOption(createdCustomerAccount));
        if (!isCompanyKind) {
          setSelectedRentalType(
            rentalTypes.find((rentalType) => rentalType.isSystemDefault && rentalType.kind === 'COMPANY') ?? null
          );
        }
        setShowNewBillingPartyDialog(false);
        setNewBillingPartyData(createEmptyReservationCustomerForm());
        setIsDirty(true);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('customers.messages.createFailed', 'Failed to create customer.')
        );
      },
    }
  );

  const handleRentalTypeChange = (nextRentalType: RentalTypeRecord | null) => {
    setSelectedRentalType(nextRentalType);
    if (nextRentalType?.kind === 'COMPANY') {
      setSelectedCustomerAccount(resolveCustomerForDriver(selectedDriver));
    } else {
      setSelectedCustomerAccount(null);
    }
    setIsDirty(true);
  };

  const handleOpenNewCustomerDialog = () => {
    setNewBillingPartyData({
      ...createEmptyReservationCustomerForm(),
      type: 'COMPANY',
      contactPersonName: selectedDriver
        ? `${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim()
        : '',
      email: selectedDriver?.email || '',
      phoneNumber: selectedDriver?.phoneNumber || '',
    });
    setShowNewBillingPartyDialog(true);
  };

  const selectedReservationBillTo = selectedReservation
    ? getReservationBillToInfo(selectedReservation)
    : null;

  const getReservationInvoiceQueryKey = (reservationId?: number | null) => [
    'reservationInvoice',
    reservationId?.toString() || 'none',
  ];

  const fetchReservationInvoice = async (reservationId: number): Promise<InvoiceRecord | null> => {
    try {
      return await invoiceApi.getByReservation(reservationId.toString());
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  };

  const { data: selectedReservationInvoice = null, isFetching: loadingSelectedReservationInvoice } =
    useQuery<InvoiceRecord | null>(
      getReservationInvoiceQueryKey(selectedReservation?.id),
      () => fetchReservationInvoice(selectedReservation!.id),
      {
        enabled: !!selectedReservation?.id && !isCreating,
        retry: false,
        refetchOnWindowFocus: false,
      }
    );

  const createReservationInvoiceMutation = useMutation(
    (reservationId: number) => invoiceApi.createFromReservations([reservationId]),
    {
      onSuccess: async (createdInvoice, reservationId) => {
        queryClient.setQueryData(getReservationInvoiceQueryKey(reservationId), createdInvoice);
        await queryClient.invalidateQueries(['invoices', companyId]);
        await queryClient.invalidateQueries(['uninvoicedReservations', companyId]);
        if (selectedReservation?.id === reservationId) {
          setDetailTab(1);
        }
        toast.success(
          t('rental.messages.invoiceCreated', {
            defaultValue: 'Invoice created successfully.',
          })
        );
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error ||
            t('rental.messages.invoiceCreateFailed', {
              defaultValue: 'Failed to create invoice.',
            })
        );
      },
    }
  );

  const markReservationInvoicePaidMutation = useMutation(
    ({ id, paymentData }: { id: string; paymentData: { paymentMethod: string; paymentReference?: string; notes?: string } }) =>
      invoiceApi.markAsPaid(id, paymentData),
    {
      onSuccess: async (updatedInvoice) => {
        if (selectedReservation?.id) {
          queryClient.setQueryData(getReservationInvoiceQueryKey(selectedReservation.id), updatedInvoice);
        }
        await queryClient.invalidateQueries(['invoices', companyId]);
        await queryClient.invalidateQueries(['uninvoicedReservations', companyId]);
        toast.success(t('invoices.messages.paid'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('invoices.messages.paidFailed'));
      },
    }
  );

  const uploadDocumentsMutation = useMutation(
    () => {
      if (!selectedReservation?.id) {
        return Promise.reject(new Error('No rental selected'));
      }

      return documentApi.uploadReservationDocuments({
        reservationId: selectedReservation.id.toString(),
        files: selectedDocumentFiles,
        onUploadProgress: setDocumentUploadProgress,
      });
    },
    {
      onSuccess: async (createdDocuments) => {
        await queryClient.invalidateQueries(['reservationDocuments', selectedReservation?.id?.toString()]);
        setSelectedDocumentFiles([]);
        setDocumentUploadProgress(0);
        toast.success(
          createdDocuments.length > 1
            ? t('rental.documents.uploadSuccessMany', { count: createdDocuments.length, defaultValue: `${createdDocuments.length} documents uploaded successfully` })
            : t('rental.documents.uploadSuccess', { defaultValue: 'Document uploaded successfully' })
        );
      },
      onError: (error: any) => {
        setDocumentUploadProgress(0);
        toast.error(error.response?.data?.error || t('rental.documents.uploadFailed', { defaultValue: 'Failed to upload rental documents' }));
      },
    }
  );

  const deleteDocumentMutation = useMutation(
    (documentId: string) => documentApi.deleteManagedDocument(documentId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['reservationDocuments', selectedReservation?.id?.toString()]);
        toast.success(t('rental.documents.deleteSuccess', { defaultValue: 'Document deleted successfully' }));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.documents.deleteFailed', { defaultValue: 'Failed to delete document' }));
      },
    }
  );

  const deleteReservationMutation = useMutation(
    (id: string) => reservationApi.cancel(id),
    {
      onSuccess: (updatedReservation) => {
        setSelectedReservation(updatedReservation);
        queryClient.invalidateQueries(['companyReservations', companyId]);
        queryClient.invalidateQueries(['reservationHistory', updatedReservation.id.toString()]);
        toast.success(t('rental.messages.cancelled'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('rental.messages.cancelFailed'));
      },
    }
  );

  // Handlers

  const handleRowClick = (reservation: Reservation) => {
    // Check for unsaved changes
    if (isDirty && (isCreating || editMode)) {
      setPendingAction(() => () => {
        loadReservation(reservation);
        setShowUnsavedDialog(false);
        setPendingAction(null);
      });
      setShowUnsavedDialog(true);
      return;
    }
    loadReservation(reservation);
  };

  const loadReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setEditMode(true); // Enable edit mode when viewing existing reservation
    setDetailTab(0);
    setIsCreating(false);
    setIsDirty(false);
    // Clear previous rental's services immediately to avoid showing stale data when switching
    setServiceItems([]);
    setServiceRows([]);
    // Initialize form data from selected reservation
    if (reservation.vehicle) {
      setSelectedVehicle({
        id: reservation.vehicle.id,
        make: reservation.vehicle.make,
        model: reservation.vehicle.model,
        year: reservation.vehicle.year,
        licensePlate: reservation.vehicle.licensePlate,
        dailyRate: reservation.vehicle.dailyRate,
        companyLocationId: reservation.vehicle.companyLocationId,
        companyLocationName: reservation.vehicle.companyLocationName,
      } as Vehicle);
    }
    setSelectedDriver(toDriverSelection(reservation.driver));
    const reservationUsesSeparateCustomer = reservation.customerSameAsDriver === false;
    setSelectedRentalType(resolveRentalTypeForReservation(reservation));
    setSelectedCustomerAccount(
      reservationUsesSeparateCustomer && reservation.customer
        ? toCustomerOptionFromReservation(reservation.customer)
        : null
    );
    setStartDate(reservation.startDate ? dayjs(reservation.startDate) : null);
    setEndDate(reservation.endDate ? dayjs(reservation.endDate) : null);
    setStartTime(reservation.pickupTime ? dayjs(reservation.pickupTime) : dayjs().hour(9).minute(0));
    setEndTime(reservation.returnTime ? dayjs(reservation.returnTime) : dayjs().hour(17).minute(0));
    setReturnDateUnknown(!reservation.endDate);
    setRentalFormData({
      insuranceIncluded: reservation.insuranceIncluded || false,
      notes: reservation.notes || '',
      status: reservation.status || ReservationStatus.CONFIRMED,
    });
    if (multipleLocationsEnabled) {
      const matchedPickupLocationId =
        reservation.pickupLocationId ??
        findLocationIdBySnapshot(pickupLocationOptions, reservation.pickupLocation);
      const nextPickupLocationId =
        matchedPickupLocationId ?? getPreferredPickupLocationId(reservation.vehicle as Vehicle);
      const matchedReturnLocationId =
        reservation.returnLocationId ??
        findLocationIdBySnapshot(returnLocationOptions, reservation.returnLocation);
      const nextReturnLocationId =
        matchedReturnLocationId ?? getPreferredReturnLocationId(nextPickupLocationId);
      const pickupIsOther = !matchedPickupLocationId && Boolean(reservation.pickupLocation?.trim());
      const returnIsOther = !matchedReturnLocationId && Boolean(reservation.returnLocation?.trim());
      setSelectedPickupLocationId(pickupIsOther ? null : nextPickupLocationId);
      setSelectedReturnLocationId(returnIsOther ? null : nextReturnLocationId);
      setIsPickupLocationOther(pickupIsOther);
      setIsReturnLocationOther(returnIsOther);
    } else {
      setSelectedPickupLocationId(null);
      setSelectedReturnLocationId(null);
      setIsPickupLocationOther(false);
      setIsReturnLocationOther(false);
    }
    // Populate inspection data from reservation
    const inspectionDataToSet = {
      mileage: reservation.pickupMileage?.toString() || '',
      fuelLevel: reservation.fuelLevelPickup || 'FULL',
      returnMileage: reservation.returnMileage?.toString() || '',
      returnFuelLevel: reservation.fuelLevelReturn || 'FULL',
    };
    setInspectionData(inspectionDataToSet);

    // Load service items for this reservation
    if (reservation.id) {
      queryClient.prefetchQuery(
        ['reservationServiceItems', reservation.id.toString()],
        () => reservationApi.getServiceItems(reservation.id.toString())
      );
    }
  };

  const handleNewRental = () => {
    // Check for unsaved changes
    if (isDirty && (isCreating || editMode)) {
      setPendingAction(() => () => {
        startNewRental();
        setShowUnsavedDialog(false);
        setPendingAction(null);
      });
      setShowUnsavedDialog(true);
      return;
    }
    startNewRental();
  };

  const startNewRental = (prefill?: RentalTimelinePrefill) => {
    setIsCreating(true);
    setSelectedReservation(null);
    setSelectedDriver(null);
    setSelectedRentalType(resolveDefaultRentalType());
    setSelectedCustomerAccount(null);
    setSelectedVehicle(prefill?.vehicle ?? null);
    setSelectedPickupLocationId(null);
    setSelectedReturnLocationId(null);
    setIsPickupLocationOther(false);
    setIsReturnLocationOther(false);
    setDetailTab(0);
    setIsDirty(false);
    resetForm(prefill);
    if (
      prefill?.vehicle &&
      prefill.vehicle.mileage !== undefined &&
      prefill.vehicle.mileage !== null
    ) {
      setInspectionData((previous) => ({
        ...previous,
        mileage: prefill.vehicle.mileage.toString(),
      }));
    }
  };

  useEffect(() => {
    const rentalPrefill = locationState?.rentalPrefill;
    if (!rentalPrefill) {
      return;
    }

    const prefillKey = `${location.key}:${rentalPrefill.vehicle.id}:${rentalPrefill.startDate}:${rentalPrefill.endDate}`;
    if (appliedRentalPrefillRef.current === prefillKey) {
      return;
    }

    const nextStartDate = dayjs(rentalPrefill.startDate).isValid()
      ? dayjs(rentalPrefill.startDate).startOf('day')
      : dayjs().startOf('day');
    const requestedEndDate = dayjs(rentalPrefill.endDate).isValid()
      ? dayjs(rentalPrefill.endDate).startOf('day')
      : nextStartDate.add(1, 'day');
    const nextEndDate = requestedEndDate.isAfter(nextStartDate, 'day')
      ? requestedEndDate
      : nextStartDate.add(1, 'day');

    appliedRentalPrefillRef.current = prefillKey;
    setIsCreating(true);
    setSelectedReservation(null);
    setSelectedDriver(null);
    setSelectedRentalType(resolveDefaultRentalType());
    setSelectedCustomerAccount(null);
    setSelectedVehicle(rentalPrefill.vehicle);
    setDetailTab(0);
    setIsDirty(false);
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setStartTime(dayjs().hour(9).minute(0));
    setEndTime(dayjs().hour(17).minute(0));
    setReturnDateUnknown(false);
    setBookingPickerDraftStart(nextStartDate);
    setBookingPickerDraftEnd(nextEndDate);
    setBookingPickerAnchorEl(null);
    setRentalFormData({
      insuranceIncluded: false,
      notes: '',
      status: ReservationStatus.CONFIRMED,
    });
    setSelectedRentalType(resolveDefaultRentalType());
    setSelectedCustomerAccount(null);
    setSelectedPickupLocationId(null);
    setSelectedReturnLocationId(null);
    setIsPickupLocationOther(false);
    setIsReturnLocationOther(false);
    setInspectionData({
      mileage:
        rentalPrefill.vehicle.mileage !== undefined && rentalPrefill.vehicle.mileage !== null
          ? rentalPrefill.vehicle.mileage.toString()
          : '',
      fuelLevel: 'FULL',
      returnMileage: '',
      returnFuelLevel: 'FULL',
    });
    setDefects([]);
    setServiceItems([]);
    setServiceRows([]);
    setShowPickupInspection(false);
    setShowDefects(false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.key, location.pathname, locationState, navigate]);

  function combineDateAndTime(date: Dayjs | null, time: Dayjs | null): Dayjs | null {
    if (!date) {
      return null;
    }

    if (!time) {
      return date.second(0).millisecond(0);
    }

    return date
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);
  }

  const handleSave = async () => {
    if (isCreating) {
      // Create new rental
      if (!selectedDriver || !selectedVehicle) {
        toast.error(t('rental.validation.selectDriverVehicle', 'Please select both a driver and a vehicle.'));
        return;
      }

      if (!startDate || (!returnDateUnknown && !endDate)) {
        toast.error(t('rental.validation.selectDates'));
        return;
      }

      if (!returnDateUnknown && endDate && (endDate.isBefore(startDate) || endDate.isSame(startDate))) {
        toast.error(t('rental.validation.endDateAfterStart'));
        return;
      }
      
      const startDateTime = combineDateAndTime(startDate, startTime) || dayjs();
      const endDateTime = returnDateUnknown ? null : (combineDateAndTime(endDate, endTime) || dayjs().add(1, 'day'));

      if (endDateTime && (endDateTime.isBefore(startDateTime) || endDateTime.isSame(startDateTime))) {
        toast.error(t('rental.validation.endDateTimeAfterStart'));
        return;
      }

      if (multipleLocationsEnabled && pickupLocationOptions.length === 0) {
        toast.error(
          t('locations.validation.noPickupLocations', {
            defaultValue: 'Add at least one active pickup location before creating rentals.',
          })
        );
        return;
      }

      if (multipleLocationsEnabled && returnLocationOptions.length === 0) {
        toast.error(
          t('locations.validation.noReturnLocations', {
            defaultValue: 'Add at least one active return location before creating rentals.',
          })
        );
        return;
      }

      if (multipleLocationsEnabled && (!hasPickupLocationSelection || !hasReturnLocationSelection)) {
        toast.error(
          t('locations.validation.selectRentalLocations', {
            defaultValue: 'Select both pickup and return locations for this rental.',
          })
        );
        return;
      }

      if (isCompanyKind && !selectedCustomerAccount) {
        toast.error(
          t('customers.validation.selectCustomer', {
            defaultValue: 'Select a customer or keep the customer the same as the driver.',
          })
        );
        return;
      }

      const rentalData: ReservationCreateRequest = {
        vehicleId: selectedVehicle.id,
        driverId: selectedDriver.id,
        customerSameAsDriver: !isCompanyKind,
        customerId: isCompanyKind ? selectedCustomerAccount?.id ?? null : null,
        rentalTypeId: selectedRentalType?.id ?? null,
        startDate: startDateTime.format('YYYY-MM-DD'),
        pickupTime: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        insuranceIncluded: rentalFormData.insuranceIncluded,
        notes: rentalFormData.notes,
        returnDateUnknown,
      };
      if (multipleLocationsEnabled) {
        const otherLocationLabel = t('locations.otherLocation', { defaultValue: 'Other' });
        rentalData.pickupLocationId = isPickupLocationOther ? undefined : selectedPickupLocation?.id;
        rentalData.returnLocationId = isReturnLocationOther ? undefined : selectedReturnLocation?.id;
        rentalData.pickupLocation = isPickupLocationOther
          ? otherLocationLabel
          : formatCompanyLocationLabel(selectedPickupLocation);
        rentalData.returnLocation = isReturnLocationOther
          ? otherLocationLabel
          : formatCompanyLocationLabel(selectedReturnLocation);
      } else {
        rentalData.pickupLocation = selectedVehicle.pickupLocation || t('common.companyLocation', 'Company Location');
        rentalData.returnLocation = selectedVehicle.pickupLocation || t('common.companyLocation', 'Company Location');
      }
      if (endDateTime) {
        rentalData.endDate = endDateTime.format('YYYY-MM-DD');
        rentalData.returnTime = endDateTime.format('YYYY-MM-DDTHH:mm:ss');
      }

      // Capture service rows before mutation (they might be cleared by resetForm)
      serviceRowsToSaveRef.current = [...serviceRows];
      
      createReservationMutation.mutate(rentalData);
    } else if (editMode && selectedReservation) {
      // Update existing rental
      if (!selectedDriver || !selectedVehicle) {
        toast.error(t('rental.validation.updateDriverVehicle', 'Please select both a driver and a vehicle.'));
        return;
      }

      if (!startDate || (!returnDateUnknown && !endDate)) {
        toast.error(t('rental.validation.selectDates'));
        return;
      }

      if (!returnDateUnknown && endDate && (endDate.isBefore(startDate) || endDate.isSame(startDate))) {
        toast.error(t('rental.validation.endDateAfterStart'));
        return;
      }

      const startDateTime = combineDateAndTime(startDate, startTime) || dayjs();
      const endDateTime = returnDateUnknown ? null : (combineDateAndTime(endDate, endTime) || dayjs().add(1, 'day'));

      if (endDateTime && (endDateTime.isBefore(startDateTime) || endDateTime.isSame(startDateTime))) {
        toast.error(t('rental.validation.endDateTimeAfterStart'));
        return;
      }

      if (multipleLocationsEnabled && pickupLocationOptions.length === 0) {
        toast.error(
          t('locations.validation.noPickupLocations', {
            defaultValue: 'Add at least one active pickup location before updating rentals.',
          })
        );
        return;
      }

      if (multipleLocationsEnabled && returnLocationOptions.length === 0) {
        toast.error(
          t('locations.validation.noReturnLocations', {
            defaultValue: 'Add at least one active return location before updating rentals.',
          })
        );
        return;
      }

      if (multipleLocationsEnabled && (!hasPickupLocationSelection || !hasReturnLocationSelection)) {
        toast.error(
          t('locations.validation.selectRentalLocations', {
            defaultValue: 'Select both pickup and return locations for this rental.',
          })
        );
        return;
      }

      if (isCompanyKind && !selectedCustomerAccount) {
        toast.error(
          t('customers.validation.selectCustomer', {
            defaultValue: 'Select a customer or keep the customer the same as the driver.',
          })
        );
        return;
      }

      // Save defects if any
      if (defects.length > 0 && selectedReservation.vehicle) {
        for (const defect of defects) {
          try {
            await createDefectMutation.mutateAsync({
              vehicleId: (selectedReservation.vehicle as any).id,
              companyId: companyId!,
              type: defect.type!,
              severity: defect.severity!,
              title: defect.title!,
              description: defect.description!,
              location: defect.location,
              component: defect.component,
              reportedDuringReservationId: selectedReservation.id,
              safetyImpact: defect.safetyImpact || false,
              operationalImpact: defect.operationalImpact || false,
              customerImpact: defect.customerImpact || false,
              vehicleOutOfService: defect.vehicleOutOfService || false,
            });
          } catch (error) {
            console.error('Error creating defect:', error);
          }
        }
      }

      // Update reservation data
      // Send dates in YYYY-MM-DD format (LocalDate expects date only, not ISO string with time)
      const updateData: ReservationUpdateRequest = {
        vehicleId: selectedVehicle?.id,
        driverId: selectedDriver.id,
        customerSameAsDriver: !isCompanyKind,
        customerId: isCompanyKind ? selectedCustomerAccount?.id ?? null : null,
        rentalTypeId: selectedRentalType?.id ?? null,
        startDate: startDateTime.format('YYYY-MM-DD'),
        pickupTime: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        notes: rentalFormData.notes,
        insuranceIncluded: rentalFormData.insuranceIncluded,
        status: rentalFormData.status,
        returnDateUnknown,
      };
      if (multipleLocationsEnabled) {
        const otherLocationLabel = t('locations.otherLocation', { defaultValue: 'Other' });
        updateData.pickupLocationId = isPickupLocationOther ? null : selectedPickupLocation?.id ?? null;
        updateData.returnLocationId = isReturnLocationOther ? null : selectedReturnLocation?.id ?? null;
        updateData.pickupLocation = isPickupLocationOther
          ? otherLocationLabel
          : formatCompanyLocationLabel(selectedPickupLocation);
        updateData.returnLocation = isReturnLocationOther
          ? otherLocationLabel
          : formatCompanyLocationLabel(selectedReturnLocation);
      }
      if (endDateTime) {
        updateData.endDate = endDateTime.format('YYYY-MM-DD');
        updateData.returnTime = endDateTime.format('YYYY-MM-DDTHH:mm:ss');
      } else {
        updateData.endDate = null;
        updateData.returnTime = null;
      }

      // Check and include pickup mileage if it's been set
      let pickupMileageValue: number | undefined;
      if (inspectionData.mileage !== undefined && inspectionData.mileage !== null && inspectionData.mileage !== '') {
        const mileageStr = inspectionData.mileage.toString().trim();
        if (mileageStr !== '') {
          const mileageValue = parseInt(mileageStr);
          if (!isNaN(mileageValue)) {
            pickupMileageValue = mileageValue;
          }
        }
      }

      // Check and include return mileage if it's been set
      let returnMileageValue: number | undefined;
      if (inspectionData.returnMileage !== undefined && inspectionData.returnMileage !== null && inspectionData.returnMileage !== '') {
        const returnMileageStr = inspectionData.returnMileage.toString().trim();
        if (returnMileageStr !== '') {
          const mileageValue = parseInt(returnMileageStr);
          if (!isNaN(mileageValue)) {
            returnMileageValue = mileageValue;
          }
        }
      }

      // Check pickup mileage and warn if needed
      if (pickupMileageValue !== undefined) {
        const canProceed = checkMileageAndWarn(pickupMileageValue, 'pickup', () => {
          // This will be called after user confirms
          const finalUpdateData: ReservationUpdateRequest = { ...updateData };
          finalUpdateData.pickupMileage = pickupMileageValue;
          if (inspectionData.fuelLevel && inspectionData.fuelLevel.trim() !== '') {
            finalUpdateData.fuelLevelPickup = inspectionData.fuelLevel;
          }
          if (returnMileageValue !== undefined) {
            finalUpdateData.returnMileage = returnMileageValue;
          }
          if (inspectionData.returnFuelLevel && inspectionData.returnFuelLevel.trim() !== '') {
            finalUpdateData.fuelLevelReturn = inspectionData.returnFuelLevel;
          }
          serviceRowsToSaveRef.current = [...serviceRows];
          updateReservationMutation.mutate({ 
            id: selectedReservation.id.toString(), 
            data: finalUpdateData 
          });
        });
        if (!canProceed) {
          return; // Wait for user confirmation
        }
        updateData.pickupMileage = pickupMileageValue;
      }

      // Check return mileage and warn if needed (only if pickup wasn't checked)
      if (returnMileageValue !== undefined && pickupMileageValue === undefined) {
        const canProceed = checkMileageAndWarn(returnMileageValue, 'return', () => {
          // This will be called after user confirms
          const finalUpdateData: ReservationUpdateRequest = { ...updateData };
          finalUpdateData.returnMileage = returnMileageValue;
          if (inspectionData.returnFuelLevel && inspectionData.returnFuelLevel.trim() !== '') {
            finalUpdateData.fuelLevelReturn = inspectionData.returnFuelLevel;
          }
          serviceRowsToSaveRef.current = [...serviceRows];
          updateReservationMutation.mutate({ 
            id: selectedReservation.id.toString(), 
            data: finalUpdateData 
          });
        });
        if (!canProceed) {
          return; // Wait for user confirmation
        }
        updateData.returnMileage = returnMileageValue;
      } else if (returnMileageValue !== undefined) {
        // If pickup was already checked, just add return mileage
        updateData.returnMileage = returnMileageValue;
      }

      // Add fuel levels
      if (inspectionData.fuelLevel && inspectionData.fuelLevel.trim() !== '') {
        updateData.fuelLevelPickup = inspectionData.fuelLevel;
      }
      if (inspectionData.returnFuelLevel && inspectionData.returnFuelLevel.trim() !== '') {
        updateData.fuelLevelReturn = inspectionData.returnFuelLevel;
      }

      // Capture service rows before mutation
      serviceRowsToSaveRef.current = [...serviceRows];

      updateReservationMutation.mutate({ 
        id: selectedReservation.id.toString(), 
        data: updateData 
      });
    }
  };

  const handleDelete = () => {
    if (selectedReservation && window.confirm(t('rental.confirmCancelReservation', 'Are you sure you want to cancel this reservation?'))) {
      deleteReservationMutation.mutate(selectedReservation.id.toString());
    }
  };

  const handleOpenDocumentsDialog = () => {
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectRentalFirst', { defaultValue: 'Select a rental first.' }));
      return;
    }
    setOpenDocumentsDialog(true);
  };

  const handleCloseDocumentsDialog = () => {
    if (uploadDocumentsMutation.isLoading) {
      return;
    }
    setOpenDocumentsDialog(false);
    setSelectedDocumentFiles([]);
    setDocumentUploadProgress(0);
  };

  const handleUploadDocuments = () => {
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectRentalFirst', { defaultValue: 'Select a rental first.' }));
      return;
    }
    if (selectedDocumentFiles.length === 0) {
      toast.error(t('rental.documents.selectFile', { defaultValue: 'Choose at least one document first.' }));
      return;
    }
    uploadDocumentsMutation.mutate();
  };

  const handlePreviewDocument = async (managedDocument: ManagedDocument) => {
    try {
      const blob = await documentApi.previewManagedDocument(managedDocument.id.toString());
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rental.documents.previewFailed', { defaultValue: 'Failed to preview document' }));
    }
  };

  const handleDownloadDocument = async (managedDocument: ManagedDocument) => {
    try {
      const blob = await documentApi.downloadManagedDocument(managedDocument.id.toString());
      const url = URL.createObjectURL(blob);
      const primaryFile = managedDocument.files[0];
      const fileName = safeFilePart(
        managedDocument.displayName || primaryFile?.originalFileName,
        `rental-document-${managedDocument.id}`
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rental.documents.downloadFailed', { defaultValue: 'Failed to download document' }));
    }
  };

  const handleDeleteDocument = (managedDocument: ManagedDocument) => {
    if (!window.confirm(t('rental.documents.confirmDelete', { defaultValue: 'Delete this document?' }))) {
      return;
    }
    deleteDocumentMutation.mutate(managedDocument.id.toString());
  };

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const handleOpenEmailDialog = () => {
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectReservationToPrint'));
      return;
    }
    setEmailDialogOpen(true);
  };

  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);

  const handleOpenWhatsAppDialog = () => {
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectReservationToPrint'));
      return;
    }
    setWhatsappDialogOpen(true);
  };

  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenPrintMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectReservationToPrint'));
      return;
    }
    setPrintMenuAnchorEl(event.currentTarget);
  };

  const handleClosePrintMenu = () => {
    setPrintMenuAnchorEl(null);
  };

  const openPdfBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const previewWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (!previewWindow) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const ensureSelectedReservationInvoice = async (createIfMissing = false): Promise<InvoiceRecord | null> => {
    if (!selectedReservation?.id) {
      toast.error(
        t('rental.validation.selectRentalFirst', {
          defaultValue: 'Select a rental first.',
        })
      );
      return null;
    }

    const reservationId = selectedReservation.id;
    const existingInvoice =
      selectedReservationInvoice ??
      (await queryClient.fetchQuery(
        getReservationInvoiceQueryKey(reservationId),
        () => fetchReservationInvoice(reservationId)
      ));

    if (existingInvoice) {
      return existingInvoice;
    }

    if (!createIfMissing) {
      toast.error(
        t('rental.validation.createInvoiceFirst', {
          defaultValue: 'Create an invoice for this rental first.',
        })
      );
      return null;
    }

    return createReservationInvoiceMutation.mutateAsync(reservationId);
  };

  const downloadAndPrintPdf = async (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Avoid opening the temporary blob URL in a second tab. In setups where the browser
    // is already configured to open downloaded PDFs, showing the blob created a duplicate
    // view of the same file and exposed an ugly blob: address to the user.

    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleCreateOrOpenInvoice = async () => {
    if (!selectedReservation) {
      toast.error(
        t('rental.validation.selectRentalFirst', {
          defaultValue: 'Select a rental first.',
        })
      );
      return;
    }

    if (!selectedReservationInvoice) {
      try {
        await createReservationInvoiceMutation.mutateAsync(selectedReservation.id);
      } catch {
        // Error toast is handled by the mutation.
      }
      return;
    }

    try {
      const blob = await invoiceApi.downloadPdf(selectedReservationInvoice.id.toString());
      const fileName = `invoice-${safeFilePart(
        selectedReservationInvoice.invoiceNumber,
        selectedReservationInvoice.id.toString()
      )}.pdf`;
      openPdfBlob(blob, fileName);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t('rental.messages.invoiceOpenFailed', {
            defaultValue: 'Failed to open invoice.',
          })
      );
    }
  };

  const handleInvoicePaidChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (!selectedReservationInvoice) {
      return;
    }

    if (!checked || selectedReservationInvoice.status === 'PAID') {
      return;
    }

    markReservationInvoicePaidMutation.mutate({
      id: selectedReservationInvoice.id.toString(),
      paymentData: {
        paymentMethod: selectedReservationInvoice.paymentMethod || 'CASH',
        paymentReference: selectedReservationInvoice.paymentReference || '',
        notes: selectedReservationInvoice.paymentNotes || '',
      },
    });
  };

  const handlePrintOrderConfirmation = async () => {
    handleClosePrintMenu();
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectReservationToPrint'));
      return;
    }

    try {
      const blob = await reservationApi.downloadOrderConfirmationPdf(selectedReservation.id.toString());
      const fileName = `order-confirmation-${safeFilePart(
        selectedReservation.reservationNumber,
        selectedReservation.id.toString()
      )}.pdf`;
      await downloadAndPrintPdf(blob, fileName);
      toast.success(t('rental.messages.orderConfirmationDownloaded'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rental.messages.orderConfirmationDownloadFailed'));
    }
  };

  const handlePrintContract = async () => {
    handleClosePrintMenu();
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectReservationToPrint'));
      return;
    }

    try {
      const blob = await reservationApi.downloadContractPdf(selectedReservation.id.toString());
      const fileName = `contract-${safeFilePart(
        selectedReservation.reservationNumber,
        selectedReservation.id.toString()
      )}.pdf`;
      await downloadAndPrintPdf(blob, fileName);
      toast.success(t('rental.messages.contractDownloaded'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rental.messages.contractDownloadFailed'));
    }
  };

  const handlePrintInvoice = async () => {
    handleClosePrintMenu();
    if (!selectedReservation) {
      toast.error(t('rental.validation.selectReservationToPrint'));
      return;
    }

    try {
      const invoice = await ensureSelectedReservationInvoice(true);
      if (!invoice) {
        return;
      }

      const blob = await invoiceApi.downloadPdf(invoice.id.toString());
      const fileName = `invoice-${safeFilePart(invoice.invoiceNumber, invoice.id.toString())}.pdf`;
      await downloadAndPrintPdf(blob, fileName);
      toast.success(
        t('rental.messages.invoicePrintReady', {
          defaultValue: 'Invoice opened for printing.',
        })
      );
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          t('rental.messages.invoicePrintFailed', {
            defaultValue: 'Failed to print invoice.',
          })
      );
    }
  };

  const handlePickup = async () => {
    if (!selectedReservation || !inspectionData.fuelLevel) {
      toast.error(t('rental.validation.inspectionRequired'));
      return;
    }

    let pickupMileageValue: number | undefined;
    if (inspectionData.mileage && inspectionData.mileage.trim() !== '') {
      pickupMileageValue = parseInt(inspectionData.mileage);
      if (isNaN(pickupMileageValue)) {
        toast.error(t('rental.validation.validPickupMileage'));
        return;
      }
    }

    if (pickupMileageValue !== undefined) {
      // Check mileage and warn if needed
      const canProceed = checkMileageAndWarn(pickupMileageValue, 'pickup', () => {
        // Create defects first if any
        if (defects.length > 0 && selectedReservation.vehicle) {
          defects.forEach((defect) => {
            createDefectMutation.mutate({
              vehicleId: (selectedReservation.vehicle as any).id,
              companyId: companyId!,
              type: defect.type!,
              severity: defect.severity!,
              title: defect.title!,
              description: defect.description!,
              location: defect.location,
              component: defect.component,
              reportedDuringReservationId: selectedReservation.id,
              safetyImpact: defect.safetyImpact || false,
              operationalImpact: defect.operationalImpact || false,
              customerImpact: defect.customerImpact || false,
              vehicleOutOfService: defect.vehicleOutOfService || false,
            });
          });
        }

        startReservationMutation.mutate({
          pickupMileage: pickupMileageValue,
          fuelLevel: inspectionData.fuelLevel,
        });
      });

      if (!canProceed) {
        return; // Wait for user confirmation
      }
    }

    // Create defects first if any
    if (defects.length > 0 && selectedReservation.vehicle) {
      for (const defect of defects) {
        try {
          await createDefectMutation.mutateAsync({
            vehicleId: (selectedReservation.vehicle as any).id,
            companyId: companyId!,
            type: defect.type!,
            severity: defect.severity!,
            title: defect.title!,
            description: defect.description!,
            location: defect.location,
            component: defect.component,
            reportedDuringReservationId: selectedReservation.id,
            safetyImpact: defect.safetyImpact || false,
            operationalImpact: defect.operationalImpact || false,
            customerImpact: defect.customerImpact || false,
            vehicleOutOfService: defect.vehicleOutOfService || false,
          });
        } catch (error) {
          console.error('Error creating defect:', error);
        }
      }
    }

    startReservationMutation.mutate({
      pickupMileage: pickupMileageValue,
      fuelLevel: inspectionData.fuelLevel,
    });
  };

  const handleReturn = async () => {
    if (!selectedReservation || !inspectionData.returnFuelLevel) {
      toast.error(t('rental.validation.returnInspectionRequired'));
      return;
    }

    let returnMileageValue: number | undefined;
    if (inspectionData.returnMileage && inspectionData.returnMileage.trim() !== '') {
      returnMileageValue = parseInt(inspectionData.returnMileage);
      if (isNaN(returnMileageValue)) {
        toast.error(t('rental.validation.validReturnMileage'));
        return;
      }
    }

    if (returnMileageValue !== undefined) {
      // Check mileage and warn if needed
      const canProceed = checkMileageAndWarn(returnMileageValue, 'return', () => {
        // Create defects first if any
        if (defects.length > 0 && selectedReservation.vehicle) {
          defects.forEach((defect) => {
            createDefectMutation.mutate({
              vehicleId: (selectedReservation.vehicle as any).id,
              companyId: companyId!,
              type: defect.type!,
              severity: defect.severity!,
              title: defect.title!,
              description: defect.description!,
              location: defect.location,
              component: defect.component,
              reportedDuringReservationId: selectedReservation.id,
              safetyImpact: defect.safetyImpact || false,
              operationalImpact: defect.operationalImpact || false,
              customerImpact: defect.customerImpact || false,
              vehicleOutOfService: defect.vehicleOutOfService || false,
            });
          });
        }

        completeReservationMutation.mutate({
          returnMileage: returnMileageValue,
          fuelLevel: inspectionData.returnFuelLevel,
        });
      });

      if (!canProceed) {
        return; // Wait for user confirmation
      }
    }

    // Create defects first if any
    if (defects.length > 0 && selectedReservation.vehicle) {
      for (const defect of defects) {
        try {
          await createDefectMutation.mutateAsync({
            vehicleId: (selectedReservation.vehicle as any).id,
            companyId: companyId!,
            type: defect.type!,
            severity: defect.severity!,
            title: defect.title!,
            description: defect.description!,
            location: defect.location,
            component: defect.component,
            reportedDuringReservationId: selectedReservation.id,
            safetyImpact: defect.safetyImpact || false,
            operationalImpact: defect.operationalImpact || false,
            customerImpact: defect.customerImpact || false,
            vehicleOutOfService: defect.vehicleOutOfService || false,
          });
        } catch (error) {
          console.error('Error creating defect:', error);
        }
      }
    }

    completeReservationMutation.mutate({
      returnMileage: returnMileageValue,
      fuelLevel: inspectionData.returnFuelLevel,
    });
  };

  const handleAddDefect = () => {
    setEditingDefectIndex(null);
    setDefectFormData({
      type: DefectType.OTHER,
      severity: DefectSeverity.LOW,
      title: '',
      description: '',
      location: '',
      component: '',
      safetyImpact: false,
      operationalImpact: false,
      customerImpact: false,
      vehicleOutOfService: false,
    });
    setShowDefectDialog(true);
  };

  const handleSaveDefect = () => {
    if (!defectFormData.title || !defectFormData.description) {
      toast.error(t('rental.validation.defectRequired'));
      return;
    }

    if (editingDefectIndex !== null) {
      const updatedDefects = [...defects];
      updatedDefects[editingDefectIndex] = defectFormData;
      setDefects(updatedDefects);
    } else {
      setDefects([...defects, defectFormData]);
    }

    setShowDefectDialog(false);
    setEditingDefectIndex(null);
  };

  const handleDeleteDefect = (index: number) => {
    setDefects(defects.filter((_, i) => i !== index));
  };

  // Service Items handlers
  // Fetch service items for selected reservation
  const { data: reservationServiceItems = [], refetch: refetchServiceItems } = useQuery(
    ['reservationServiceItems', selectedReservation?.id?.toString()],
    () => {
      if (selectedReservation?.id) {
        return reservationApi.getServiceItems(selectedReservation.id.toString());
      }
      return Promise.resolve([]);
    },
    { enabled: !!selectedReservation?.id }
  );

  const {
    data: reservationHistory = [],
    isLoading: loadingReservationHistory,
  } = useQuery(
    ['reservationHistory', selectedReservation?.id?.toString()],
    () => {
      if (selectedReservation?.id) {
        return reservationApi.getHistory(selectedReservation.id.toString());
      }
      return Promise.resolve([]);
    },
    { enabled: !!selectedReservation?.id && !isCreating }
  );

  const {
    data: reservationDocuments = [],
    isLoading: isLoadingReservationDocuments,
  } = useQuery(
    ['reservationDocuments', selectedReservation?.id?.toString()],
    () => {
      if (selectedReservation?.id) {
        return reservationApi.getDocuments(selectedReservation.id.toString());
      }
      return Promise.resolve([]);
    },
    { enabled: openDocumentsDialog && !!selectedReservation?.id }
  );

  const {
    data: driverLicenseDocuments = [],
    isLoading: isLoadingDriverLicenseDocuments,
  } = useQuery(
    ['driverLicenseDocuments', selectedReservation?.driverId?.toString()],
    () => {
      if (selectedReservation?.driverId) {
        return documentApi.getDriverLicenseDocuments(selectedReservation.driverId.toString());
      }
      return Promise.resolve([]);
    },
    { enabled: openDocumentsDialog && !!selectedReservation?.driverId }
  );

  // Existing driver license photos not already listed among this rental's own documents
  const reusableDriverLicenseDocuments = driverLicenseDocuments.filter(
    (licenseDocument) => !reservationDocuments.some((doc) => doc.id === licenseDocument.id)
  );

  // Fetch service templates - always enabled for dropdown
  const { data: serviceTemplates = [] } = useQuery(
    'serviceTemplates',
    () => serviceTemplateApi.getActive()
  );

  // Helper function to calculate rental days (inclusive of both pickup and return days)
  // e.g. Pickup March 1 9:00, Return March 7 17:00 = 7 days (customer has car on March 1, 2, 3, 4, 5, 6, 7)
  const calculateDays = (start: Dayjs | null, end: Dayjs | null) => {
    if (!start || !end) return 0;
    const startDay = start.startOf('day');
    const endDay = end.startOf('day');
    const diff = endDay.diff(startDay, 'day');
    const days = diff + 1; // Inclusive: both pickup day and return day count
    return days > 0 ? days : 1; // Minimum 1 day
  };

  const getRentalTemplate = () => serviceTemplates.find((template: ServiceTemplate) => template.serviceType === 'RENTAL');

  const hasRentalContext = Boolean(selectedVehicle && startDate && (endDate || returnDateUnknown));
  const canEditServicePrice = (row: ServiceRow) => row.allowPriceChange ?? !row.templateId;

  const buildRentalServiceRow = (ready = hasRentalContext): ServiceRow => {
    const rentalTemplate = getRentalTemplate();
    const dailyRate = ready
      ? (typeof selectedVehicle?.dailyRate === 'string'
          ? parseFloat(selectedVehicle.dailyRate)
          : selectedVehicle?.dailyRate || 0)
      : 0;
    const licensePlate = ready ? ((selectedVehicle as any)?.licensePlate || t('common.unavailable', 'N/A')) : '';
    const description = ready && selectedVehicle ? `${licensePlate} - ${selectedVehicle.make} ${selectedVehicle.model}` : '';

    return {
      templateId: rentalTemplate?.id?.toString(),
      allowPriceChange: rentalTemplate?.allowPriceChange ?? true,
      serviceType: 'RENTAL',
      serviceName: rentalTemplate?.serviceName || t('serviceTypes.rental'),
      description,
      unitPrice: dailyRate,
      quantity: ready ? (returnDateUnknown ? 1 : (calculateDays(startDate, endDate) || 1)) : 0,
      serviceLocation: '',
      isNew: ready,
      isEditing: false,
      isPendingRental: !ready,
    };
  };

  // Update rental service quantity when dates change
  useEffect(() => {
    if (startDate && (endDate || returnDateUnknown) && serviceRows.length > 0) {
      const days = returnDateUnknown ? 1 : calculateDays(startDate, endDate);
      setServiceRows(prevRows => {
        const updatedRows = prevRows.map(row => {
          if (row.serviceType === 'RENTAL' && !row.isPendingRental && row.quantity !== days) {
            return { ...row, quantity: days };
          }
          return row;
        });
        // Only return new array if something changed
        const hasChanged = updatedRows.some((row, index) => 
          row.quantity !== prevRows[index].quantity
        );
        return hasChanged ? updatedRows : prevRows;
      });
    }
  }, [startDate, endDate, returnDateUnknown]);

  // Track which rows were just saved to prevent immediate overwrite
  const recentlySavedRowsRef = useRef<Set<number>>(new Set());
  const serviceRowsToSaveRef = useRef<ServiceRow[]>([]);
  
  // Update service items when query data changes (only if not currently editing or saving)
  useEffect(() => {
    if (reservationServiceItems) {
      setServiceItems(reservationServiceItems);
      
      // Only sync serviceRows if no rows are currently being edited or are new
      const hasEditingRows = serviceRows.some(row => row.isEditing || row.isNew);
      const hasRecentlySavedRows = recentlySavedRowsRef.current.size > 0;
      // Don't overwrite user's local edits (e.g. unit price for RENTAL) - preserves changes until Save
      if (isDirty) {
        return;
      }
      
      // Don't sync if we have recently saved rows (wait a bit for the refetch to complete)
      if (hasRecentlySavedRows) {
        // Clear the recently saved flag after a delay
        setTimeout(() => {
          recentlySavedRowsRef.current.clear();
        }, 500);
        return;
      }
      
      if (!hasEditingRows) {
        // Convert service items to rows for inline editing
        setServiceRows(reservationServiceItems.map(item => ({
          id: item.id,
          templateId: item.serviceTemplateId ? item.serviceTemplateId.toString() : undefined,
          allowPriceChange: item.allowPriceChange ?? !item.serviceTemplateId,
          serviceType: item.serviceType,
          serviceName: item.serviceName,
          description: item.description,
          unitPrice: item.unitPrice,
          quantity: item.quantity,
          serviceLocation: item.serviceLocation,
          isNew: false,
          isEditing: false,
        })));
      } else {
        // Update only non-editing rows, preserve editing and new rows
        const updatedRows = serviceRows.map((existingRow, idx) => {
          if (existingRow.isEditing || existingRow.isNew || recentlySavedRowsRef.current.has(idx)) {
            return existingRow; // Keep editing/new/recently saved rows unchanged
          }
          // Find matching item from server
          const serverItem = reservationServiceItems.find(item => item.id === existingRow.id);
          if (serverItem) {
            // Only update if the server data is actually different (avoid unnecessary updates)
            const needsUpdate = 
              serverItem.serviceType !== existingRow.serviceType ||
              serverItem.serviceName !== existingRow.serviceName ||
              serverItem.description !== existingRow.description ||
              Math.abs((serverItem.unitPrice || 0) - (existingRow.unitPrice || 0)) > 0.01 ||
              serverItem.quantity !== existingRow.quantity ||
              serverItem.serviceLocation !== existingRow.serviceLocation ||
              (serverItem.allowPriceChange ?? !serverItem.serviceTemplateId) !== (existingRow.allowPriceChange ?? !existingRow.templateId) ||
              (serverItem.serviceTemplateId ? serverItem.serviceTemplateId.toString() : undefined) !== existingRow.templateId;
            
            if (needsUpdate) {
              return {
                id: serverItem.id,
                templateId: serverItem.serviceTemplateId ? serverItem.serviceTemplateId.toString() : undefined,
                allowPriceChange: serverItem.allowPriceChange ?? !serverItem.serviceTemplateId,
                serviceType: serverItem.serviceType,
                serviceName: serverItem.serviceName,
                description: serverItem.description,
                unitPrice: serverItem.unitPrice,
                quantity: serverItem.quantity,
                serviceLocation: serverItem.serviceLocation,
                isNew: false,
                isEditing: false,
              };
            }
          }
          return existingRow;
        });
        
        // Add any new items from server that don't exist locally (but not if we have unsaved new rows)
        const hasNewRows = serviceRows.some(row => row.isNew);
        if (!hasNewRows) {
          reservationServiceItems.forEach(serverItem => {
            if (!updatedRows.find(row => row.id === serverItem.id)) {
              updatedRows.push({
                id: serverItem.id,
                templateId: serverItem.serviceTemplateId ? serverItem.serviceTemplateId.toString() : undefined,
                allowPriceChange: serverItem.allowPriceChange ?? !serverItem.serviceTemplateId,
                serviceType: serverItem.serviceType,
                serviceName: serverItem.serviceName,
                description: serverItem.description,
                unitPrice: serverItem.unitPrice,
                quantity: serverItem.quantity,
                serviceLocation: serverItem.serviceLocation,
                isNew: false,
                isEditing: false,
              });
            }
          });
        }
        
        setServiceRows(updatedRows);
      }
    } else {
      // Only clear if not editing
      if (!serviceRows.some(row => row.isEditing || row.isNew)) {
        setServiceRows([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationServiceItems]);

  // Add the default Rental service row only once vehicle and dates are ready.
  useEffect(() => {
    if (!isCreating) {
      return;
    }

    if (!hasRentalContext) {
      setServiceRows(prevRows => prevRows.filter(row => row.serviceType !== 'RENTAL'));
      return;
    }

    setServiceRows(prevRows => {
      const rentalRowIndex = prevRows.findIndex(row => row.serviceType === 'RENTAL');
      const nextRentalRow = buildRentalServiceRow(true);

      if (rentalRowIndex < 0) {
        return [nextRentalRow, ...prevRows];
      }

      const currentRentalRow = prevRows[rentalRowIndex];
      const hasChanged =
        currentRentalRow.templateId !== nextRentalRow.templateId ||
        currentRentalRow.serviceName !== nextRentalRow.serviceName ||
        currentRentalRow.description !== nextRentalRow.description ||
        currentRentalRow.unitPrice !== nextRentalRow.unitPrice ||
        currentRentalRow.quantity !== nextRentalRow.quantity ||
        currentRentalRow.isNew !== nextRentalRow.isNew ||
        currentRentalRow.isPendingRental !== nextRentalRow.isPendingRental;

      if (!hasChanged) {
        return prevRows;
      }

      const updatedRows = [...prevRows];
      updatedRows[rentalRowIndex] = {
        ...currentRentalRow,
        ...nextRentalRow,
        id: currentRentalRow.id,
      };
      return updatedRows;
    });
  }, [isCreating, selectedVehicle, startDate, endDate, returnDateUnknown, serviceTemplates]);

  // Auto-update Rental service description when vehicle or dates change (only if not being edited)
  useEffect(() => {
    if (!isCreating && selectedVehicle && startDate && (endDate || returnDateUnknown) && serviceRows.length > 0) {
      const rentalRowIndex = serviceRows.findIndex(row => row.serviceType === 'RENTAL');
      
      // Only auto-update if Rental service exists and is NOT being edited
      if (rentalRowIndex >= 0) {
        const rentalRow = serviceRows[rentalRowIndex];
        
        // Don't auto-update if the row is being edited or is new
        if (rentalRow.isEditing || rentalRow.isNew) {
          return;
        }
        
        const dailyRate = typeof selectedVehicle.dailyRate === 'string' 
          ? parseFloat(selectedVehicle.dailyRate) 
          : selectedVehicle.dailyRate || 0;
        const licensePlate = (selectedVehicle as any).licensePlate || t('common.unavailable', 'N/A');
        const newDescription = `${licensePlate} - ${selectedVehicle.make} ${selectedVehicle.model}`;
        
        // Only update if description changed (don't auto-update price/quantity if user has edited them)
        // Don't set isDirty - this is a programmatic sync to match vehicle, not a user edit
        if (rentalRow.description !== newDescription) {
          const updatedRows = [...serviceRows];
          updatedRows[rentalRowIndex] = {
            ...updatedRows[rentalRowIndex],
            description: newDescription,
          };
          setServiceRows(updatedRows);
        }
      }
    }
  }, [selectedVehicle, startDate, endDate, returnDateUnknown]);

  const handleAddServiceRow = () => {
    if (isCreating && !hasRentalContext) {
      toast.error(t('rental.validation.selectDates'));
      return;
    }

    setServiceRows([...serviceRows, {
      allowPriceChange: true,
      serviceType: SERVICE_TYPES.OTHER,
      serviceName: '',
      description: '',
      unitPrice: 0,
      quantity: 1,
      serviceLocation: '',
      isNew: true,
      isEditing: true,
    }]);
    setIsDirty(true);
  };

  const generateRentalServiceDescription = (): string => {
    if (!selectedVehicle) {
      return '';
    }
    
    const licensePlate = (selectedVehicle as any).licensePlate || t('common.unavailable', 'N/A');
    
    return `${licensePlate} - ${selectedVehicle.make} ${selectedVehicle.model}`;
  };

  const handleServiceRowChange = (index: number, field: string, value: any) => {
    const updatedRows = [...serviceRows];
    updatedRows[index] = { ...updatedRows[index], [field]: value };

    if (field === 'templateId' && !value) {
      updatedRows[index] = {
        ...updatedRows[index],
        templateId: undefined,
        allowPriceChange: true,
      };
    }

    // If service template is selected, auto-fill other fields
    if (field === 'templateId' && value) {
      const template = serviceTemplates.find((t: ServiceTemplate) => t.id === parseInt(value));
      if (template) {
        let description = template.description || '';
        
        // Special handling for Rental service - generate description with vehicle details
        if (template.serviceType === 'RENTAL') {
          description = generateRentalServiceDescription();
          // Also set unit price from vehicle daily rate if available
          const vehiclePriceRaw = selectedVehicle?.dailyRate || template.defaultPrice;
          const vehiclePrice = typeof vehiclePriceRaw === 'string' 
            ? parseFloat(vehiclePriceRaw) 
            : (typeof vehiclePriceRaw === 'number' ? vehiclePriceRaw : 0);
          const days = returnDateUnknown ? 1 : (calculateDays(startDate, endDate) || 1);
          updatedRows[index] = {
            ...updatedRows[index],
            templateId: value,
            allowPriceChange: template.allowPriceChange,
            serviceType: template.serviceType,
            serviceName: template.serviceName,
            description: description,
            unitPrice: vehiclePrice,
            quantity: days,
          };
        } else {
          updatedRows[index] = {
            ...updatedRows[index],
            templateId: value,
            allowPriceChange: template.allowPriceChange,
            serviceType: template.serviceType,
            serviceName: template.serviceName,
            description: description,
            unitPrice: template.defaultPrice,
          };
        }
      }
    }
    
    // Auto-update Rental service description when vehicle or dates change
    if (updatedRows[index].serviceType === 'RENTAL') {
      const rentalDescription = generateRentalServiceDescription();
      if (rentalDescription) {
        updatedRows[index].description = rentalDescription;
      }
    }
    
    setServiceRows(updatedRows);
    setIsDirty(true);
  };

  const handleSaveServiceRow = async (index: number) => {
    const row = serviceRows[index];

    if (!row.serviceName || row.unitPrice == null || Number.isNaN(row.unitPrice)) {
      toast.error(t('rental.validation.serviceRequired'));
      return;
    }

    if (isCreating) {
      // Just mark as saved locally for now
      const updatedRows = [...serviceRows];
      updatedRows[index] = { ...updatedRows[index], isNew: false, isEditing: false };
      setServiceRows(updatedRows);
      toast.success(t('rental.messages.serviceAddedPending'));
      return;
    }

    if (!selectedReservation?.id) {
      toast.error(t('rental.messages.saveReservationFirst'));
      return;
    }

    try {
      const serviceData: ReservationServiceItemCreateRequest = {
        serviceTemplateId: row.templateId ? parseInt(row.templateId) : undefined,
        serviceType: row.serviceType,
        serviceName: row.serviceName,
        description: row.description,
        unitPrice: row.unitPrice,
        quantity: row.quantity,
        serviceLocation: row.serviceLocation,
      };

      let savedServiceItem: ReservationServiceItem;
      if (row.id) {
        // Update existing
        savedServiceItem = await reservationApi.updateServiceItem(row.id.toString(), serviceData);
        toast.success(t('rental.messages.serviceUpdated'));
      } else {
        // Create new
        savedServiceItem = await reservationApi.addServiceItem(selectedReservation.id.toString(), serviceData);
        toast.success(t('rental.messages.serviceAdded'));
      }
      
      // Mark this row as recently saved to prevent immediate overwrite
      recentlySavedRowsRef.current.add(index);
      
      // Update local state immediately with saved values from server response
      const updatedRows = [...serviceRows];
      updatedRows[index] = { 
        id: savedServiceItem.id!,
        templateId: savedServiceItem.serviceTemplateId ? savedServiceItem.serviceTemplateId.toString() : undefined,
        allowPriceChange: savedServiceItem.allowPriceChange ?? !savedServiceItem.serviceTemplateId,
        serviceType: savedServiceItem.serviceType,
        serviceName: savedServiceItem.serviceName,
        description: savedServiceItem.description,
        unitPrice: savedServiceItem.unitPrice,
        quantity: savedServiceItem.quantity,
        serviceLocation: savedServiceItem.serviceLocation,
        isNew: false, 
        isEditing: false 
      };
      setServiceRows(updatedRows);
      setIsDirty(true);
      
      // Invalidate queries but don't immediately refetch - let the user see their saved changes
      queryClient.invalidateQueries(['reservationServiceItems', selectedReservation.id.toString()]);
      queryClient.invalidateQueries(['companyReservations', companyId]);
      
      // Clear the recently saved flag after a delay to allow future updates
      setTimeout(() => {
        recentlySavedRowsRef.current.delete(index);
      }, 1000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rental.messages.saveServiceFailed'));
    }
  };

  const handleDeleteServiceRow = async (index: number) => {
    const row = serviceRows[index];
    
    // Prevent deletion of rental service item
    if (row.serviceType === 'RENTAL') {
      toast.error(t('rental.messages.cannotDeleteRentalService'));
      return;
    }

    if (row.isNew) {
      // Just remove from local state
      setServiceRows(serviceRows.filter((_, i) => i !== index));
      setIsDirty(true);
      return;
    }

    if (!row.id) {
      setServiceRows(serviceRows.filter((_, i) => i !== index));
      return;
    }

    if (!window.confirm(t('rental.confirmDeleteService', 'Are you sure you want to delete this service?'))) {
      return;
    }

    try {
      await reservationApi.deleteServiceItem(row.id.toString());
      toast.success(t('rental.messages.serviceDeleted'));
      setServiceRows(serviceRows.filter((_, i) => i !== index));
      setIsDirty(true);
      
      // Refetch after a short delay to allow state to settle
      setTimeout(() => {
        refetchServiceItems();
        queryClient.invalidateQueries(['companyReservations', companyId]);
      }, 100);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('rental.messages.deleteServiceFailed'));
    }
  };

  const handleCancelEditServiceRow = (index: number) => {
    const row = serviceRows[index];
    if (row.isNew) {
      // Remove new row if cancelled
      setServiceRows(serviceRows.filter((_, i) => i !== index));
    } else {
      // Reset to original values
      const originalItem = serviceItems.find(item => item.id === row.id);
      if (originalItem) {
        const updatedRows = [...serviceRows];
        updatedRows[index] = {
          id: originalItem.id,
          templateId: originalItem.serviceTemplateId ? originalItem.serviceTemplateId.toString() : undefined,
          allowPriceChange: originalItem.allowPriceChange ?? !originalItem.serviceTemplateId,
          serviceType: originalItem.serviceType,
          serviceName: originalItem.serviceName,
          description: originalItem.description,
          unitPrice: originalItem.unitPrice,
          quantity: originalItem.quantity,
          serviceLocation: originalItem.serviceLocation,
          isNew: false,
          isEditing: false,
        };
        setServiceRows(updatedRows);
      }
    }
  };

  const resetForm = (prefill?: RentalTimelinePrefill) => {
    const nextStartDate = prefill?.startDate && dayjs(prefill.startDate).isValid()
      ? dayjs(prefill.startDate).startOf('day')
      : dayjs().startOf('day');
    const requestedEndDate = prefill?.endDate && dayjs(prefill.endDate).isValid()
      ? dayjs(prefill.endDate).startOf('day')
      : nextStartDate.add(1, 'day');
    const nextEndDate = requestedEndDate.isAfter(nextStartDate, 'day')
      ? requestedEndDate
      : nextStartDate.add(1, 'day');

    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setStartTime(dayjs().hour(9).minute(0));
    setEndTime(dayjs().hour(17).minute(0));
    setReturnDateUnknown(false);
    setBookingPickerDraftStart(nextStartDate);
    setBookingPickerDraftEnd(nextEndDate);
    setBookingPickerAnchorEl(null);
    setRentalFormData({
      insuranceIncluded: false,
      notes: '',
      status: ReservationStatus.CONFIRMED,
    });
    setSelectedPickupLocationId(null);
    setSelectedReturnLocationId(null);
    setIsPickupLocationOther(false);
    setIsReturnLocationOther(false);
    setInspectionData({
      mileage: '',
      fuelLevel: 'FULL',
      returnMileage: '',
      returnFuelLevel: 'FULL',
    });
    setDefects([]);
    setServiceItems([]);
    setServiceRows([]);
    setShowPickupInspection(false);
    setShowDefects(false);
  };
  
  // Handle date change
  const handleDateChange = (newStartDate: Dayjs | null, newEndDate: Dayjs | null) => {
    if (newStartDate !== null) {
      setStartDate(newStartDate);
      if (endDate && newStartDate.isAfter(endDate)) {
        setEndDate(newStartDate.add(1, 'day'));
      }
    }
    if (newEndDate !== null) {
      setEndDate(newEndDate);
      setReturnDateUnknown(false);
    }
    
    // Update rental service row quantity when dates change
    if ((newStartDate !== null || newEndDate !== null) && serviceRows.length > 0) {
      const currentStart = newStartDate !== null ? newStartDate : startDate;
      const currentEnd = newEndDate !== null ? newEndDate : endDate;
      if (currentStart && currentEnd) {
        const days = calculateDays(currentStart, currentEnd) || 1;
        const updatedRows = serviceRows.map(row => {
          if (row.serviceType === 'RENTAL' && !row.isPendingRental) {
            return { ...row, quantity: days };
          }
          return row;
        });
        setServiceRows(updatedRows);
      }
    }
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(parseRentalTimeFieldValue(value, DEFAULT_PICKUP_TIME));
    setIsDirty(true);
  };

  const handleEndTimeChange = (value: string) => {
    setEndTime(parseRentalTimeFieldValue(value, DEFAULT_RETURN_TIME));
    setReturnDateUnknown(false);
    setIsDirty(true);
  };

  const handleToggleReturnDateUnknown = () => {
    if (returnDateUnknown) {
      const fallbackDate = startDate ? startDate.add(1, 'day') : dayjs().add(1, 'day');
      setEndDate(fallbackDate);
      setEndTime(dayjs().hour(17).minute(0));
      setReturnDateUnknown(false);
    } else {
      setEndDate(null);
      setEndTime(null);
      setReturnDateUnknown(true);
    }
    setIsDirty(true);
  };

  const handleOpenBookingDatePicker = (event: React.MouseEvent<HTMLElement>) => {
    const visibleMonth = (startDate || dayjs()).startOf('month');
    setBookingPickerDraftStart(startDate);
    setBookingPickerDraftEnd(returnDateUnknown ? null : endDate);
    setBookingPickerMonth(visibleMonth);
    setBookingPickerAnchorEl(event.currentTarget);
  };

  const handleCloseBookingDatePicker = () => {
    setBookingPickerAnchorEl(null);
  };

  const applyBookingPickerRange = (nextStartDate: Dayjs, nextEndDate: Dayjs | null) => {
    handleDateChange(nextStartDate.startOf('day'), nextEndDate ? nextEndDate.startOf('day') : null);
    if (!startTime) {
      setStartTime(dayjs().hour(9).minute(0));
    }
    if (nextEndDate) {
      setReturnDateUnknown(false);
      if (!endTime) {
        setEndTime(dayjs().hour(17).minute(0));
      }
    }
    setIsDirty(true);
  };

  const handleBookingDateSelect = (day: Dayjs) => {
    const selectedDay = day.startOf('day');
    if (isCreating && selectedDay.isBefore(dayjs().startOf('day'), 'day')) {
      return;
    }

    if (!bookingPickerDraftStart || bookingPickerDraftEnd) {
      setBookingPickerDraftStart(selectedDay);
      setBookingPickerDraftEnd(null);
      applyBookingPickerRange(selectedDay, null);
      return;
    }

    if (selectedDay.isSame(bookingPickerDraftStart, 'day') || selectedDay.isBefore(bookingPickerDraftStart, 'day')) {
      setBookingPickerDraftStart(selectedDay);
      setBookingPickerDraftEnd(null);
      applyBookingPickerRange(selectedDay, null);
      return;
    }

    setBookingPickerDraftEnd(selectedDay);
    applyBookingPickerRange(bookingPickerDraftStart, selectedDay);
    handleCloseBookingDatePicker();
  };

  const renderBookingPickerMonth = (month: Dayjs, showPreviousButton: boolean, showNextButton: boolean) => {
    const today = dayjs().startOf('day');
    const days = getBookingPickerMonthDays(month);

    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          {showPreviousButton && (
            <IconButton
              size="small"
              aria-label={t('rental.bookingPreviousMonths')}
              onClick={() => setBookingPickerMonth((currentMonth) => currentMonth.subtract(1, 'month'))}
              sx={{ position: 'absolute', left: 0 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {month.format('MMMM YYYY')}
          </Typography>
          {showNextButton && (
            <IconButton
              size="small"
              aria-label={t('rental.bookingNextMonths')}
              onClick={() => setBookingPickerMonth((currentMonth) => currentMonth.add(1, 'month'))}
              sx={{ position: 'absolute', right: 0 }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 36px)',
            justifyContent: 'center',
            rowGap: 0.75,
            columnGap: 0.5,
          }}
        >
          {BOOKING_PICKER_WEEKDAYS.map((weekday) => (
            <Typography
              key={`${month.format('YYYY-MM')}-${weekday}`}
              variant="caption"
              align="center"
              sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: '28px' }}
            >
              {weekday}
            </Typography>
          ))}

          {days.map((day, index) => {
            if (!day) {
              return <Box key={`${month.format('YYYY-MM')}-empty-${index}`} sx={{ width: 36, height: 36 }} />;
            }

            const isSelectedStart = Boolean(bookingPickerDraftStart?.isSame(day, 'day'));
            const isSelectedEnd = Boolean(bookingPickerDraftEnd?.isSame(day, 'day'));
            const isInRange = Boolean(
              bookingPickerDraftStart &&
                bookingPickerDraftEnd &&
                day.isAfter(bookingPickerDraftStart, 'day') &&
                day.isBefore(bookingPickerDraftEnd, 'day')
            );
            const isDisabled = isCreating && day.isBefore(today, 'day');

            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                component="button"
                type="button"
                disabled={isDisabled}
                aria-label={day.format('DD MMMM YYYY')}
                onClick={() => handleBookingDateSelect(day)}
                sx={{
                  width: 36,
                  height: 36,
                  p: 0,
                  border: '1px solid',
                  borderColor: isSelectedStart || isSelectedEnd ? 'primary.main' : 'transparent',
                  borderRadius: isInRange ? 1 : '50%',
                  bgcolor: isSelectedStart || isSelectedEnd ? 'primary.main' : (isInRange ? '#e7f1ff' : 'transparent'),
                  color: isSelectedStart || isSelectedEnd ? 'primary.contrastText' : (isDisabled ? 'text.disabled' : 'text.primary'),
                  cursor: isDisabled ? 'default' : 'pointer',
                  font: 'inherit',
                  fontWeight: isSelectedStart || isSelectedEnd || day.isSame(today, 'day') ? 700 : 400,
                  opacity: isDisabled ? 0.42 : 1,
                  '&:hover': {
                    bgcolor: isDisabled
                      ? 'transparent'
                      : (isSelectedStart || isSelectedEnd ? 'primary.dark' : 'action.hover'),
                  },
                }}
              >
                {day.date()}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };
  
  // Handle insurance change
  const handleInsuranceChange = (included: boolean) => {
    setRentalFormData({
      ...rentalFormData,
      insuranceIncluded: included,
    });
  };
  
  // Handle vehicle change
  const handleVehicleChange = (vehicle: Vehicle | null) => {
    setSelectedVehicle(vehicle);
    if (multipleLocationsEnabled) {
      const nextPickupLocationId = getPreferredPickupLocationId(vehicle);
      setSelectedPickupLocationId(nextPickupLocationId);
      setSelectedReturnLocationId(getPreferredReturnLocationId(nextPickupLocationId));
      setIsPickupLocationOther(false);
      setIsReturnLocationOther(false);
    } else {
      setSelectedPickupLocationId(null);
      setSelectedReturnLocationId(null);
      setIsPickupLocationOther(false);
      setIsReturnLocationOther(false);
    }
    
    // Auto-populate pickup mileage when vehicle is selected (only when creating/editing)
    // Allow 0 as a valid mileage value (check for undefined/null, not falsy)
    if (vehicle && (isCreating || editMode) && vehicle.mileage !== undefined && vehicle.mileage !== null) {
      setInspectionData(prev => ({
        ...prev,
        mileage: vehicle.mileage.toString()
      }));
    }
  };

  // Check mileage and show warning if needed
  const checkMileageAndWarn = (
    mileageValue: number,
    type: 'pickup' | 'return',
    onConfirm: () => void
  ): boolean => {
    const vehicleMileage = selectedVehicle?.mileage || 0;
    const pickupMileage = type === 'return' 
      ? (parseInt(inspectionData.mileage) || selectedReservation?.pickupMileage || 0)
      : mileageValue;
    
    let warnings: string[] = [];
    
    // Check if mileage is lower than vehicle mileage
    if (mileageValue < vehicleMileage) {
      warnings.push(
        t('rental.mileageLowerCurrent', {
          type: type === 'pickup' ? t('fields.pickup').toLowerCase() : t('fields.return').toLowerCase(),
          mileage: mileageValue.toLocaleString(),
          vehicleMileage: vehicleMileage.toLocaleString(),
          updatedMileage: Math.max(vehicleMileage, mileageValue).toLocaleString(),
        })
      );
    }
    
    // Check if return mileage is lower than pickup mileage
    if (type === 'return' && mileageValue < pickupMileage) {
      warnings.push(
        t('rental.returnLowerPickup', {
          mileage: mileageValue.toLocaleString(),
          pickupMileage: pickupMileage.toLocaleString(),
        })
      );
    }
    
    if (warnings.length > 0) {
      setMileageWarningMessage(warnings.join('\n\n'));
      setMileageWarningType(type);
      setPendingMileageAction(() => onConfirm);
      setShowMileageWarningDialog(true);
      return false; // Don't proceed yet
    }
    
    return true; // No warnings, proceed
  };

  const handleMileageWarningConfirm = () => {
    setShowMileageWarningDialog(false);
    if (pendingMileageAction) {
      pendingMileageAction();
      setPendingMileageAction(null);
    }
  };

  const handleMileageWarningCancel = () => {
    setShowMileageWarningDialog(false);
    setPendingMileageAction(null);
    setMileageWarningMessage('');
    setMileageWarningType(null);
  };

  useEffect(() => {
    if (
      isCreating &&
      selectedVehicle &&
      hasVehicleAvailabilityWindow &&
      !loadingVehicleAvailability &&
      Object.keys(vehicleAvailability).length > 0 &&
      vehicleAvailability[selectedVehicle.id] === false
    ) {
      setSelectedVehicle(null);
      setIsDirty(true);
    }
  }, [isCreating, selectedVehicle, hasVehicleAvailabilityWindow, loadingVehicleAvailability, vehicleAvailability]);

  const dateFilteredVehicles = hasVehicleAvailabilityWindow
    ? vehicles.filter((vehicle) => vehicleAvailability[vehicle.id])
    : vehicles;
  const availableVehicles = selectedVehicle && !isCreating && !dateFilteredVehicles.some((vehicle) => vehicle.id === selectedVehicle.id)
    ? [selectedVehicle, ...dateFilteredVehicles]
    : dateFilteredVehicles;
  const showServicesSection = !isCreating || hasRentalContext;

  const describeReservationHistoryEvent = (event: ReservationHistoryEvent) => {
    const nextStatus = statusLabel(t, event.newStatus);
    if (event.eventType === 'STATUS_INITIALIZED' || !event.previousStatus) {
      return t('rental.historyCreatedAs', { status: nextStatus });
    }
    return `${statusLabel(t, event.previousStatus)} -> ${nextStatus}`;
  };

  const isSelectedReservationInvoicePaid = selectedReservationInvoice?.status === 'PAID';
  const detailStatusValue =
    (isCreating || editMode)
      ? rentalFormData.status
      : selectedReservation?.status || ReservationStatus.CONFIRMED;
  const showPendingStatusActions =
    (isCreating || editMode) &&
    (selectedReservation?.status === ReservationStatus.PENDING ||
      (isCreating && rentalFormData.status === ReservationStatus.PENDING));
  const pendingStatusActionOptions = [
    ReservationStatus.CONFIRMED,
    ReservationStatus.DECLINED,
  ] as const;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', xl: '1760px' },
        mx: 'auto',
      }}
    >
      <Box>
        {/* Sticky top toolbar with primary rental actions */}
        <Box
          sx={{ 
            position: 'sticky',
            top: 64, // Position below AppBar (AppBar height is typically 64px)
            zIndex: 1100, // Higher than AppBar (which is 1100) to stay on top
            bgcolor: 'background.paper', 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            width: '100%',
            mt: 0,
            mb: 0
          }}
        >
          <Toolbar 
            sx={{ 
              pl: { xs: 2, sm: 3 }, 
              pr: { xs: 2, sm: 3 }, 
              minHeight: '48px !important', 
              py: 0.5
            }}
          >
            {/* Top action buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewRental}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.new')}</Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!isCreating && !editMode}
                color="primary"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  opacity: (isCreating || editMode) ? 1 : 0.6,
                  '&.Mui-disabled': {
                    opacity: 0.4
                  }
                }}
              >{t('common.save')}</Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={!selectedReservation}
                color="error"
                size="small"
                sx={{ 
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.delete')}</Button>
              <Button
                variant="outlined"
                startIcon={<AssignmentIcon />}
                onClick={handleOpenDocumentsDialog}
                disabled={!selectedReservation}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('rental.documents.button', { defaultValue: 'Upload document' })}</Button>
              <Button
                variant="outlined"
                startIcon={<Print fontSize="small" />}
                endIcon={<ArrowDropDown fontSize="small" />}
                onClick={handleOpenPrintMenu}
                disabled={!selectedReservation}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.print')}</Button>
              <Button
                variant="outlined"
                startIcon={<Email fontSize="small" />}
                onClick={handleOpenEmailDialog}
                disabled={!selectedReservation}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.email', 'Email')}</Button>
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />}
                onClick={handleOpenWhatsAppDialog}
                disabled={!selectedReservation}
                size="small"
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >{t('common.whatsapp', 'WhatsApp')}</Button>
            </Box>
            <Menu
              anchorEl={printMenuAnchorEl}
              open={Boolean(printMenuAnchorEl)}
              onClose={handleClosePrintMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <MenuItem onClick={handlePrintOrderConfirmation}>
                <Print fontSize="small" sx={{ mr: 1 }} />
                {t('rental.print.orderConfirmation')}
              </MenuItem>
              <MenuItem onClick={handlePrintContract}>
                <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                {t('rental.print.contract')}
              </MenuItem>
              <MenuItem
                onClick={handlePrintInvoice}
                disabled={createReservationInvoiceMutation.isLoading}
              >
                <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
                {t('rental.print.invoice', { defaultValue: 'Invoice' })}
              </MenuItem>
            </Menu>
          </Toolbar>
        </Box>

        <Box sx={{ py: 1 }}>
        <Grid container spacing={0}>
          {/* Main Table Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              {/* Secondary toolbar - quick list controls, pagination, and clear filters */}
            <Toolbar sx={{ 
              pl: { xs: 2, sm: 3 }, 
              pr: { xs: 2, sm: 3 }, 
              minHeight: '48px !important', 
              py: 0.5, 
              bgcolor: 'background.paper', 
              borderBottom: '1px solid', 
              borderColor: 'divider' 
            }}>
              {/* Secondary action buttons - left */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={() => queryClient.invalidateQueries(['companyReservations', companyId])}
                  size="small"
                  sx={{ 
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5
                    }
                  }}
                >{t('common.refresh')}</Button>
                <Tooltip title={t('common.filter', { defaultValue: 'Filter' })}>
                  <IconButton
                    size="small"
                    onClick={openRentalFilterDialog}
                    sx={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid',
                      borderColor: rentalListPopupFilterActive ? 'primary.main' : 'divider',
                      color: rentalListPopupFilterActive ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    <FilterList fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Pagination Controls - Center */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.max(0, prev - 1))}
                  disabled={page === 0}
                  sx={{ 
                    width: '28px',
                    height: '28px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowUpward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', minWidth: '80px', textAlign: 'center' }}>
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredReservations.length)} {t('common.of')} {filteredReservations.length}
                </Typography>
                
                {/* Page Numbers */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {Array.from({ length: Math.min(5, Math.ceil(filteredReservations.length / rowsPerPage)) }, (_, i) => {
                    const totalPages = Math.ceil(filteredReservations.length / rowsPerPage);
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i;
                    } else if (page < 2) {
                      pageNum = i;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    if (pageNum < 0 || pageNum >= totalPages) return null;

                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        onClick={() => setPage(pageNum)}
                        sx={{
                          minWidth: '32px',
                          height: '28px',
                          px: 1,
                          fontSize: '0.8125rem',
                          bgcolor: page === pageNum ? 'primary.main' : 'transparent',
                          color: page === pageNum ? 'white' : 'text.primary',
                          '&:hover': {
                            bgcolor: page === pageNum ? 'primary.dark' : 'action.hover'
                          }
                        }}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                  {Math.ceil(filteredReservations.length / rowsPerPage) > 5 && page < Math.ceil(filteredReservations.length / rowsPerPage) - 3 && (
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', px: 0.5 }}>
                      ...
                    </Typography>
                  )}
                  {Math.ceil(filteredReservations.length / rowsPerPage) > 5 && page < Math.ceil(filteredReservations.length / rowsPerPage) - 3 && (
                    <Button
                      size="small"
                      onClick={() => setPage(Math.ceil(filteredReservations.length / rowsPerPage) - 1)}
                      sx={{
                        minWidth: '32px',
                        height: '28px',
                        px: 1,
                        fontSize: '0.8125rem',
                      }}
                    >
                      {Math.ceil(filteredReservations.length / rowsPerPage)}
                    </Button>
                  )}
                </Box>

                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.min(Math.ceil(filteredReservations.length / rowsPerPage) - 1, prev + 1))}
                  disabled={page >= Math.ceil(filteredReservations.length / rowsPerPage) - 1}
                  sx={{ 
                    width: '28px',
                    height: '28px',
                    '&.Mui-disabled': { opacity: 0.3 }
                  }}
                >
                  <ArrowDownward sx={{ fontSize: '1rem', transform: 'rotate(-90deg)' }} />
                </IconButton>
                
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  size="small"
                  sx={{ 
                    height: '28px',
                    fontSize: '0.8125rem',
                    ml: 1,
                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                  }}
                  renderValue={(value) => t('common.rowsPerPage', { count: value, defaultValue: `${value} / page` })}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Clear Filters Button - Right */}
              <Button
                size="small"
                startIcon={<FilterListOff fontSize="small" />}
                onClick={() => {
                  setColumnFilters({
                    reservationNumber: '',
                    customer: '',
                    vehicleMake: '',
                    vehicleModel: '',
                    vehicleLicensePlate: '',
                  startDate: '',
                  endDate: '',
                  status: '',
                  total: '',
                });
                  setSortConfig({ ...DEFAULT_RENTAL_SORT_CONFIG });
                  setPage(0);
                }}
                sx={{ fontSize: '0.8125rem', height: '28px', px: 1 }}
              >{t('common.clearFilters')}</Button>
            </Toolbar>

            {/* Table */}
            <TableContainer>
              <Table 
                size="small" 
                sx={{ 
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { 
                    fontSize: '0.875rem'
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableFilterCell placeholder={t('fields.reservation')} value={columnFilters.reservationNumber} onChange={(value) => handleColumnFilterChange('reservationNumber', value)} onSort={() => handleSort('reservationNumber')} sortDirection={sortConfig?.key === 'reservationNumber' ? sortConfig.direction : null} width="9%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.driver', 'Driver')} value={columnFilters.customer} onChange={(value) => handleColumnFilterChange('customer', value)} onSort={() => handleSort('customer')} sortDirection={sortConfig?.key === 'customer' ? sortConfig.direction : null} width="14%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.make')} value={columnFilters.vehicleMake} onChange={(value) => handleColumnFilterChange('vehicleMake', value)} onSort={() => handleSort('vehicleMake')} sortDirection={sortConfig?.key === 'vehicleMake' ? sortConfig.direction : null} width="11%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.model')} value={columnFilters.vehicleModel} onChange={(value) => handleColumnFilterChange('vehicleModel', value)} onSort={() => handleSort('vehicleModel')} sortDirection={sortConfig?.key === 'vehicleModel' ? sortConfig.direction : null} width="10%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.licensePlate')} value={columnFilters.vehicleLicensePlate} onChange={(value) => handleColumnFilterChange('vehicleLicensePlate', value)} onSort={() => handleSort('vehicleLicensePlate')} sortDirection={sortConfig?.key === 'vehicleLicensePlate' ? sortConfig.direction : null} width="11%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.startDate')} value={columnFilters.startDate} onChange={(value) => handleColumnFilterChange('startDate', value)} onSort={() => handleSort('startDate')} sortDirection={sortConfig?.key === 'startDate' ? sortConfig.direction : null} width="12%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.endDate')} value={columnFilters.endDate} onChange={(value) => handleColumnFilterChange('endDate', value)} onSort={() => handleSort('endDate')} sortDirection={sortConfig?.key === 'endDate' ? sortConfig.direction : null} width="12%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.status')} value={columnFilters.status} onChange={(value) => handleColumnFilterChange('status', value)} onSort={() => handleSort('status')} sortDirection={sortConfig?.key === 'status' ? sortConfig.direction : null} width="13%" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    <TableFilterCell placeholder={t('fields.total')} value={columnFilters.total} onChange={(value) => handleColumnFilterChange('total', value)} onSort={() => handleSort('total')} sortDirection={sortConfig?.key === 'total' ? sortConfig.direction : null} width="8%" align="right" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingReservations ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : paginatedReservations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                        {t('reservationsPage.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedReservations.map((reservation) => (
                      <TableRow
                        key={reservation.id}
                        hover
                        selected={selectedReservation?.id === reservation.id}
                        onClick={() => handleRowClick(reservation)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '9%', width: '9%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {reservation.reservationNumber}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '13%', width: '13%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {reservation.driver
                              ? `${reservation.driver.firstName || ''} ${reservation.driver.lastName || ''}`.trim()
                              : reservation.guestDriverName || t('common.unavailable', 'N/A')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '11%', width: '11%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {reservation.vehicle?.make || t('common.unavailable', 'N/A')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '10%', width: '10%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {reservation.vehicle?.model || t('common.unavailable', 'N/A')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '11%', width: '11%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {reservation.vehicle?.licensePlate || t('common.unavailable', 'N/A')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '12%', width: '12%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {formatDate(reservation.startDate)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '12%', width: '12%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            {reservation.endDate ? formatDate(reservation.endDate) : t('rental.returnDateTbd')}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, px: 0.75, maxWidth: '12%', width: '12%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                          }}>
                            <ReservationStatusChip
                              status={reservation.status}
                              sx={{
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%'
                                }
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.75, px: 0.75, maxWidth: '8%', width: '8%' }}>
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                            width: '100%'
                          }}>
                            {formatCurrency(getReservationSubtotal(reservation), 0)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Detail Panel */}
        {(isCreating || selectedReservation) && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    {isCreating ? t('rental.newRental') : t('rental.numberHeading', { number: selectedReservation?.reservationNumber || '' })}
                  </Typography>
                  {rentalTypes.length > 1 && (isCreating || editMode || selectedReservation) && (
                    <FormControl size="small" sx={{ minWidth: 140, width: 140 }}>
                      <Select
                        displayEmpty
                        value={selectedRentalType?.id ?? ''}
                        onChange={(event) => {
                          if (isCreating || editMode) {
                            const nextId = Number(event.target.value);
                            const nextRentalType =
                              rentalTypes.find((rentalType) => rentalType.id === nextId) ?? null;
                            handleRentalTypeChange(nextRentalType);
                          }
                        }}
                        disabled={!isCreating && !editMode}
                        sx={{
                          height: 32,
                          '& .MuiSelect-select': {
                            py: 0.25,
                            pl: 1,
                            pr: '28px !important',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      >
                        {rentalTypes.map((rentalType) => (
                          <MenuItem key={rentalType.id} value={rentalType.id}>
                            {rentalType.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {(isCreating || editMode || selectedReservation) && (
                    showPendingStatusActions ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        {pendingStatusActionOptions.map((status) => {
                          const isSelected = rentalFormData.status === status;
                          const color = status === ReservationStatus.DECLINED ? 'error' : 'success';

                          return (
                            <Button
                              key={status}
                              size="small"
                              variant={isSelected ? 'contained' : 'outlined'}
                              color={color}
                              onClick={() => {
                                setRentalFormData({ ...rentalFormData, status });
                                setIsDirty(true);
                              }}
                              sx={{
                                height: 32,
                                px: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                              }}
                            >
                              {status === ReservationStatus.CONFIRMED
                                ? t('common.confirm')
                                : t('common.decline')}
                            </Button>
                          );
                        })}
                      </Box>
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 132, width: 132 }}>
                        <Select
                          value={detailStatusValue}
                          onChange={(e) => {
                            if (isCreating || editMode) {
                              setRentalFormData({ ...rentalFormData, status: e.target.value as ReservationStatus });
                              setIsDirty(true);
                            }
                          }}
                          disabled={!isCreating && !editMode}
                          renderValue={(value) => (
                            <ReservationStatusChip
                              status={value as ReservationStatus}
                              sx={{ height: 22, maxWidth: 96 }}
                            />
                          )}
                          sx={{
                            height: 32,
                            '& .MuiSelect-select': {
                              py: 0.25,
                              pl: 1,
                              pr: '28px !important',
                              display: 'flex',
                              alignItems: 'center',
                            },
                          }}
                        >
                          {Object.values(ReservationStatus).map((status) => (
                            <MenuItem key={status} value={status}>
                              <ReservationStatusChip status={status} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )
                  )}
                  {selectedDriver && getWhatsAppLink(selectedDriver.phoneNumber) && (
                    <Tooltip title={t('rental.openWhatsApp', 'Open WhatsApp chat')}>
                      <IconButton
                        size="small"
                        component="a"
                        href={getWhatsAppLink(selectedDriver.phoneNumber)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#25D366' }}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {selectedReservation && (
                  <Tabs
                    value={detailTab}
                    onChange={(_, value) => setDetailTab(value)}
                    sx={{ minHeight: 40 }}
                  >
                    <Tab label={t('common.details')} sx={{ minHeight: 40 }} />
                    <Tab label={t('fields.invoice', { defaultValue: 'Invoice' })} sx={{ minHeight: 40 }} />
                    <Tab label={t('rental.history')} sx={{ minHeight: 40 }} />
                    <Tab label={t('rental.emails', 'Emails')} sx={{ minHeight: 40 }} />
                  </Tabs>
                )}
              </Box>
              <Divider sx={{ mb: 1 }} />

              <TabPanel value={selectedReservation ? detailTab : 0} index={0}>
              <Grid container spacing={1} sx={RENTAL_DETAIL_UNIFORM_FIELDS_SX}>
                <Grid item xs={12} md={multipleLocationsEnabled ? 6 : 12}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1,
                      '& .MuiFormControl-root, & .MuiInputBase-root': {
                        minWidth: 0,
                      },
                    }}
                  >
                    {(isCreating || editMode) ? (
                      <Autocomplete
                        size="small"
                        fullWidth
                        disableClearable
                        sx={{
                          '& .rental-customer-edit-btn': { visibility: 'hidden' },
                          '&:hover .rental-customer-edit-btn, &:focus-within .rental-customer-edit-btn': {
                            visibility: 'visible',
                          },
                        }}
                        options={customers}
                        loading={searchingCustomers}
                        value={selectedDriver ?? undefined}
                        onChange={(_, value) => {
                          if (value && 'isRegisterNew' in value) {
                            resetNewCustomerLicenseUpload();
                            setNewCustomerData(createEmptyRentalDriverForm());
                            setShowNewDriverDialog(true);
                          } else {
                            const nextDriver = value as DriverRecord | null;
                            setSelectedDriver(nextDriver);
                            if (isCompanyKind) {
                              setSelectedCustomerAccount(resolveCustomerForDriver(nextDriver));
                            }
                            setIsDirty(true);
                          }
                        }}
                        getOptionLabel={(option) => {
                          if (option && 'isRegisterNew' in option) return '';
                          const c = option as DriverRecord;
                          return `${c.firstName} ${c.lastName} - ${c.personalIdNumber || c.email || t('rental.noContact')}`.trim();
                        }}
                        filterOptions={(options, { inputValue }) => {
                          const search = inputValue.trim().toLowerCase();
                          const filtered = search
                            ? options.filter((c) => {
                                const label = `${c.firstName} ${c.lastName} ${c.email} ${c.phoneNumber || ''} ${c.personalIdNumber || ''}`.toLowerCase();
                                return label.includes(search);
                              })
                            : options;
                          return [
                            ...filtered,
                            { id: -1, firstName: t('rental.registerNewDriver', 'New Driver'), isRegisterNew: true } as DriverRecord & { isRegisterNew: true },
                          ];
                        }}
                        isOptionEqualToValue={(option, value) => {
                          if (option && 'isRegisterNew' in option) return false;
                          return (option as DriverRecord).id === (value as DriverRecord)?.id;
                        }}
                        renderOption={(props, option) => {
                          if (option && 'isRegisterNew' in option) {
                            return (
                              <li {...props} key="register_new">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.5 }}>
                                  <PersonAdd fontSize="small" />
                                  <Typography variant="body2" color="primary">
                                    {t('rental.registerNewDriver', 'New Driver')}
                                  </Typography>
                                </Box>
                              </li>
                            );
                          }
                          const c = option as DriverRecord;
                          return (
                            <li {...props} key={c.id}>
                              {c.firstName} {c.lastName} - {c.personalIdNumber || c.email || t('rental.noContact')}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('rental.driver', 'Driver')}
                            size="small"
                            placeholder={t('placeholders.typeToSearchCustomers')}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {searchingCustomers ? <CircularProgress color="inherit" size={18} /> : null}
                                  {selectedDriver && (
                                    <Tooltip title={t('rental.viewEditDriver', 'View / edit driver')}>
                                      <IconButton
                                        size="small"
                                        className="rental-customer-edit-btn"
                                        sx={{ p: '2px' }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenDriverDetails(selectedDriver);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : selectedReservation ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={
                          selectedReservation.driver
                            ? `${selectedReservation.driver.firstName || ''} ${selectedReservation.driver.lastName || ''}`.trim() +
                              (selectedReservation.driver.phoneNumber ? ` - ${selectedReservation.driver.phoneNumber}` : '')
                            : selectedReservation.guestDriverName
                              ? `${selectedReservation.guestDriverName}${selectedReservation.guestDriverPhone ? ` - ${selectedReservation.guestDriverPhone}` : ''} (${t('rental.guest')})`
                              : t('rental.noDriverInfo', 'No driver information')
                        }
                        label={t('rental.driver', 'Driver')}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    ) : null}

                    {(isCreating || editMode) ? (
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={availableVehicles}
                        loading={loadingVehicles || loadingVehicleAvailability}
                        value={selectedVehicle}
                        onChange={(_, value) => {
                          handleVehicleChange(value || null);
                          setIsDirty(true);
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) =>
                          `${option.make} ${option.model} ${option.year} - ${formatCurrency(
                            typeof option.dailyRate === 'string' ? parseFloat(option.dailyRate) : option.dailyRate
                          )}/${t('common.day')}`
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}
                            >
                              {option.make} {option.model} {option.year} -{' '}
                              {formatCurrency(
                                typeof option.dailyRate === 'string' ? parseFloat(option.dailyRate) : option.dailyRate
                              )}
                              /{t('common.day')}
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('fields.vehicle')}
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {(loadingVehicles || loadingVehicleAvailability) ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : selectedReservation ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={`${selectedReservation.vehicle?.make} ${selectedReservation.vehicle?.model} ${selectedReservation.vehicle?.year}`}
                        label={t('fields.vehicle')}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    ) : null}
                  </Box>
                </Grid>

                {multipleLocationsEnabled && (
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={1}>
                      {!(pickupLocationOptions.length > 0 && returnLocationOptions.length > 0) && (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            {t('locations.rentalSelectionMissing', {
                              defaultValue:
                                'No active pickup/return locations are available yet. Add and activate company locations before saving rentals.',
                            })}
                          </Alert>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {t('locations.pickupLocation', {
                              defaultValue: 'Pickup Location',
                            })}
                          </InputLabel>
                          <Select
                            label={t('locations.pickupLocation', {
                              defaultValue: 'Pickup Location',
                            })}
                            value={isPickupLocationOther ? OTHER_LOCATION_VALUE : selectedPickupLocationId ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              if (nextValue === OTHER_LOCATION_VALUE) {
                                setIsPickupLocationOther(true);
                                setSelectedPickupLocationId(null);
                              } else {
                                const nextPickupLocationId =
                                  nextValue === '' ? null : Number(nextValue);
                                setIsPickupLocationOther(false);
                                setSelectedPickupLocationId(nextPickupLocationId);
                                if (!isReturnLocationOther) {
                                  setSelectedReturnLocationId(
                                    getPreferredReturnLocationId(nextPickupLocationId)
                                  );
                                }
                              }
                              setIsDirty(true);
                            }}
                          >
                            {pickupLocationOptions.map((locationOption) => (
                              <MenuItem key={locationOption.id} value={locationOption.id}>
                                {locationOption.name}
                              </MenuItem>
                            ))}
                            <MenuItem value={OTHER_LOCATION_VALUE}>
                              {t('locations.otherLocation', { defaultValue: 'Other' })}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {t('locations.returnLocation', {
                              defaultValue: 'Return Location',
                            })}
                          </InputLabel>
                          <Select
                            label={t('locations.returnLocation', {
                              defaultValue: 'Return Location',
                            })}
                            value={isReturnLocationOther ? OTHER_LOCATION_VALUE : selectedReturnLocationId ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              if (nextValue === OTHER_LOCATION_VALUE) {
                                setIsReturnLocationOther(true);
                                setSelectedReturnLocationId(null);
                              } else {
                                setIsReturnLocationOther(false);
                                setSelectedReturnLocationId(
                                  nextValue === '' ? null : Number(nextValue)
                                );
                              }
                              setIsDirty(true);
                            }}
                          >
                            {returnLocationOptions.map((locationOption) => (
                              <MenuItem key={locationOption.id} value={locationOption.id}>
                                {locationOption.name}
                              </MenuItem>
                            ))}
                            <MenuItem value={OTHER_LOCATION_VALUE}>
                              {t('locations.otherLocation', { defaultValue: 'Other' })}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {((isCreating || editMode) ? isCompanyKind : !!selectedReservationBillTo) && (
                <Grid item xs={12}>
                  {(isCreating || editMode) ? (
                    isCompanyKind ? (
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={customerAccounts}
                            loading={loadingCustomerAccounts}
                            value={selectedCustomerAccount}
                            onChange={(_, value) => {
                              if (value && 'isCreateNew' in value) {
                                handleOpenNewCustomerDialog();
                              } else {
                                setSelectedCustomerAccount(
                                  value as ReservationCustomerOption | null
                                );
                                setIsDirty(true);
                              }
                            }}
                            getOptionLabel={(option) => {
                              if (option && 'isCreateNew' in option) {
                                return '';
                              }
                              const customer = option as ReservationCustomerOption;
                              return [
                                customer.displayName,
                                customer.legalName || customer.contactPersonName || '',
                                customer.taxId ? `(${customer.taxId})` : '',
                              ]
                                .filter(Boolean)
                                .join(' - ');
                            }}
                            filterOptions={(options, { inputValue }) => {
                              const search = inputValue.trim().toLowerCase();
                              const filtered = search
                                ? options.filter((customer) => {
                                    const label = [
                                      customer.displayName,
                                      customer.legalName,
                                      customer.contactPersonName,
                                      customer.email,
                                      customer.phoneNumber,
                                      customer.taxId,
                                      customer.registrationNumber,
                                    ]
                                      .filter(Boolean)
                                      .join(' ')
                                      .toLowerCase();
                                    return label.includes(search);
                                  })
                                : options;

                              return [
                                ...filtered,
                                {
                                  id: -1,
                                  type: 'COMPANY',
                                  displayName: t(
                                    'customers.actions.createFromRental',
                                    'Create new customer'
                                  ),
                                  active: true,
                                  totalReservations: 0,
                                  isCreateNew: true,
                                } as ReservationCustomerOption & { isCreateNew: true },
                              ];
                            }}
                            isOptionEqualToValue={(option, value) => {
                              if (option && 'isCreateNew' in option) {
                                return false;
                              }
                              return (
                                (option as ReservationCustomerOption).id ===
                                (value as ReservationCustomerOption)?.id
                              );
                            }}
                            renderOption={(props, option) => {
                              if (option && 'isCreateNew' in option) {
                                return (
                                  <li {...props} key="create_customer">
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: '100%',
                                        py: 0.5,
                                      }}
                                    >
                                      <AddIcon fontSize="small" />
                                      <Typography variant="body2" color="primary">
                                        {t(
                                          'customers.actions.createFromRental',
                                          'Create new customer'
                                        )}
                                      </Typography>
                                    </Box>
                                  </li>
                                );
                              }

                              const customer = option as ReservationCustomerOption;
                              return (
                                <li {...props} key={customer.id}>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                      {customer.displayName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {[
                                        customer.contactPersonName || customer.legalName || '',
                                        customer.email || customer.phoneNumber || '',
                                      ]
                                        .filter(Boolean)
                                        .join(' | ') || t('common.unavailable', 'Unavailable')}
                                    </Typography>
                                  </Box>
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('fields.customer', 'Customer')}
                                placeholder={t(
                                  'customers.placeholders.search',
                                  'Search customers'
                                )}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingCustomerAccounts ? (
                                        <CircularProgress color="inherit" size={18} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                    ) : null
                  ) : selectedReservationBillTo ? (
                    <TextField
                      fullWidth
                      size="small"
                      label={t('fields.customer', 'Customer')}
                      value={
                        selectedReservationBillTo.contactPersonName
                          ? `${selectedReservationBillTo.displayName} (${selectedReservationBillTo.contactPersonName})`
                          : selectedReservationBillTo.displayName
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  ) : null}
                </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {(isCreating || editMode) ? (
                            <>
                              <TextField
                                fullWidth
                                required
                                size="small"
                                label={t('search.dateRangeLabel')}
                                placeholder={t('search.dateRangeLabel')}
                                value={rentalDateRangeText}
                                onClick={handleOpenBookingDatePicker}
                                InputProps={{
                                  readOnly: true,
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <CalendarMonthIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip title={returnDateUnknown ? t('rental.setReturnDate') : t('rental.markReturnDateUnknown')}>
                                        <IconButton
                                          size="small"
                                          color={returnDateUnknown ? 'primary' : 'default'}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleToggleReturnDateUnknown();
                                          }}
                                          aria-label={returnDateUnknown ? t('rental.setReturnDate') : t('rental.markReturnDateUnknown')}
                                          edge="end"
                                        >
                                          <EventBusyIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                                inputProps={{
                                  'aria-label': t('search.dateRangeLabel'),
                                }}
                                sx={CLICKABLE_BOOKING_FIELD_SX}
                              />
                              <Popover
                                open={bookingPickerOpen}
                                anchorEl={bookingPickerAnchorEl}
                                onClose={handleCloseBookingDatePicker}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                PaperProps={{
                                  sx: {
                                    width: { xs: 'calc(100vw - 32px)', md: 760 },
                                    maxWidth: 'calc(100vw - 32px)',
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0 16px 36px rgba(15, 23, 42, 0.2)',
                                  },
                                }}
                              >
                                <Box sx={{ p: 2.5 }}>
                                  <Box
                                    sx={{
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      align="center"
                                      sx={{
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        pb: 1.5,
                                        borderBottom: '2px solid',
                                        borderColor: 'primary.main',
                                      }}
                                    >
                                      {t('rental.bookingCalendar')}
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                      gap: { xs: 2.5, md: 4 },
                                      pt: 2.5,
                                      pb: 2,
                                    }}
                                  >
                                    {renderBookingPickerMonth(bookingPickerMonth, true, false)}
                                    {renderBookingPickerMonth(bookingPickerMonth.add(1, 'month'), false, true)}
                                  </Box>
                                </Box>
                              </Popover>
                            </>
                          ) : selectedReservation ? (
                            <TextField
                              fullWidth
                              size="small"
                              label={t('search.dateRangeLabel')}
                              value={
                                selectedReservation.startDate
                                  ? `${formatDate(selectedReservation.startDate)} - ${
                                      selectedReservation.endDate
                                        ? formatDate(selectedReservation.endDate)
                                        : t('rental.returnDateTbd')
                                    }`
                                  : ''
                              }
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          ) : null}
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1,
                            '& .MuiFormControl-root, & .MuiInputBase-root': {
                              minWidth: 0,
                            },
                          }}
                        >
                          {(isCreating || editMode) ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="time"
                              label={t('fields.startTime')}
                              value={getRentalTimeFieldValue(startTime, DEFAULT_PICKUP_TIME)}
                              onChange={(e) => handleStartTimeChange(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              sx={RENTAL_TIME_FIELD_SX}
                            />
                          ) : selectedReservation ? (
                            <TextField
                              fullWidth
                              size="small"
                              label={t('fields.startTime')}
                              value={selectedReservation.pickupTime ? dayjs(selectedReservation.pickupTime).format('HH:mm') : DEFAULT_PICKUP_TIME}
                              InputProps={{ readOnly: true }}
                            />
                          ) : null}

                          {(isCreating || editMode) ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="time"
                              label={t('fields.endTime')}
                              value={returnDateUnknown ? '' : getRentalTimeFieldValue(endTime, DEFAULT_RETURN_TIME)}
                              onChange={(e) => handleEndTimeChange(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                              disabled={returnDateUnknown}
                              sx={RENTAL_TIME_FIELD_SX}
                            />
                          ) : selectedReservation ? (
                            <TextField
                              fullWidth
                              size="small"
                              label={t('fields.endTime')}
                              value={selectedReservation.endDate
                                ? (selectedReservation.returnTime ? dayjs(selectedReservation.returnTime).format('HH:mm') : DEFAULT_RETURN_TIME)
                                : ''}
                              InputProps={{ readOnly: true }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        className="rental-detail-notes"
                        fullWidth
                        size="small"
                        label={t('fields.notes')}
                        multiline
                        minRows={1}
                        value={isCreating ? rentalFormData.notes : (editMode ? rentalFormData.notes : (selectedReservation?.notes || ''))}
                        onChange={(e) => {
                          if (isCreating || editMode) {
                            setRentalFormData({ ...rentalFormData, notes: e.target.value });
                            setIsDirty(true);
                          }
                        }}
                        placeholder={t('placeholders.notes')}
                        disabled={!isCreating && !editMode}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1,
                      '& .MuiFormControl-root, & .MuiInputBase-root': {
                        minWidth: 0,
                      },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label={t('fields.pickupMileageKm')}
                      type="number"
                      value={inspectionData.mileage || (selectedReservation?.pickupMileage ?? '')}
                      onChange={(e) => {
                        setInspectionData({ ...inspectionData, mileage: e.target.value });
                        setIsDirty(true);
                      }}
                      InputProps={{
                        startAdornment: <Speed sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                      disabled={!isCreating && !editMode && selectedReservation?.status === ReservationStatus.COMPLETED && !!selectedReservation.pickupMileage}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label={t('fields.returnMileageKm')}
                      type="number"
                      value={inspectionData.returnMileage || (selectedReservation?.returnMileage ?? '')}
                      onChange={(e) => {
                        setInspectionData({ ...inspectionData, returnMileage: e.target.value });
                        setIsDirty(true);
                      }}
                      InputProps={{
                        startAdornment: <Speed sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                      disabled={!isCreating && !editMode && selectedReservation?.status === ReservationStatus.COMPLETED && !!selectedReservation.returnMileage}
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel>{t('fields.pickupFuelLevel')}</InputLabel>
                      <Select
                        value={inspectionData.fuelLevel || (selectedReservation?.fuelLevelPickup || 'FULL')}
                        label={t('fields.pickupFuelLevel')}
                        onChange={(e) => {
                          setInspectionData({ ...inspectionData, fuelLevel: e.target.value });
                          setIsDirty(true);
                        }}
                        startAdornment={<LocalGasStation sx={{ mr: 1, color: 'text.secondary' }} />}
                        sx={{ '& .MuiSelect-select': { py: 1 } }}
                        disabled={!isCreating && !editMode && selectedReservation?.status === ReservationStatus.COMPLETED && !!selectedReservation.fuelLevelPickup}
                      >
                        {FUEL_LEVEL_OPTIONS.map((level) => (
                          <MenuItem key={`pickup-${level}`} value={level}>
                            {enumLabel(t, 'fuelLevels', level)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>{t('fields.returnFuelLevel')}</InputLabel>
                      <Select
                        value={inspectionData.returnFuelLevel || (selectedReservation?.fuelLevelReturn || 'FULL')}
                        label={t('fields.returnFuelLevel')}
                        onChange={(e) => {
                          setInspectionData({ ...inspectionData, returnFuelLevel: e.target.value });
                          setIsDirty(true);
                        }}
                        startAdornment={<LocalGasStation sx={{ mr: 1, color: 'text.secondary' }} />}
                        sx={{ '& .MuiSelect-select': { py: 1 } }}
                        disabled={!isCreating && !editMode && selectedReservation?.status === ReservationStatus.COMPLETED && !!selectedReservation.fuelLevelReturn}
                      >
                        {FUEL_LEVEL_OPTIONS.map((level) => (
                          <MenuItem key={`return-${level}`} value={level}>
                            {enumLabel(t, 'fuelLevels', level)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>

              {/* Services Section */}
              {showServicesSection && (
              <Box sx={{ width: '100%', mt: 2 }}>
                    <Divider sx={{ my: 1 }} />
                    
                    {serviceRows.length === 0 ? (
                      isCreating ? null : (
                        <Alert
                        severity="info"
                        sx={{ py: 0 }}
                        action={
                          <Tooltip title={t('rental.addService')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleAddServiceRow}
                              aria-label={t('rental.addService')}
                              sx={{ mt: -0.25 }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        {t('rental.noServices')}
                      </Alert>
                      )
                    ) : (
                      <Paper variant="outlined" sx={{ p: 0.75 }}>
                        <TableContainer sx={{ pt: 0.5 }}>
                          <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableBody>
                              {serviceRows.map((row, index) => {
                                const isPendingRental = row.serviceType === 'RENTAL' && !!row.isPendingRental;
                                const isEditable = !isPendingRental && (row.isEditing || row.isNew || (!isCreating && editMode));
                                const isPriceEditable = isEditable && canEditServicePrice(row);
                                const totalPrice = (row.unitPrice || 0) * (row.quantity || 1);
                                
                                return (
                                  <TableRow
                                    key={row.id || index}
                                    hover
                                    sx={{
                                      '& td': {
                                        pt: index === 0 ? 1 : 0.5,
                                        pb: 0.5,
                                        px: 0.75,
                                        verticalAlign: 'middle',
                                        borderBottom: 'none',
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ width: '18%' }}>
                                      {isEditable ? (
                                        <FormControl fullWidth size="small">
                                          <InputLabel shrink>{t('fields.service')}</InputLabel>
                                          <Select
                                            value={row.templateId || ''}
                                            onChange={(e) => handleServiceRowChange(index, 'templateId', e.target.value)}
                                            displayEmpty
                                            label={t('fields.service')}
                                            disabled={row.serviceType === 'RENTAL'}
                                            sx={{ 
                                              height: '40px',
                                              '& .MuiSelect-select': { py: 0.75 }
                                            }}
                                          >
                                            <MenuItem value="">
                                              <em>{t('rental.selectService')}</em>
                                            </MenuItem>
                                            {serviceTemplates.map((template: ServiceTemplate) => (
                                              <MenuItem key={template.id} value={template.id.toString()}>
                                                {template.serviceName}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      ) : (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={t('fields.service')}
                                          value={row.serviceName || ''}
                                          InputProps={{ readOnly: true }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ width: '32%' }}>
                                      {isEditable ? (
                                        <TextField
                                          size="small"
                                          fullWidth
                                          label={t('fields.description')}
                                          value={row.description || ''}
                                          onChange={(e) => handleServiceRowChange(index, 'description', e.target.value)}
                                          placeholder={t('placeholders.serviceDescription')}
                                          disabled={row.serviceType === 'RENTAL'}
                                          sx={{ 
                                            '& .MuiInputBase-root': { height: '40px' },
                                            '& .MuiInputBase-input': { py: 0.75 }
                                          }}
                                        />
                                      ) : (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={t('fields.description')}
                                          value={isPendingRental ? '' : (row.description || '-')}
                                          InputProps={{ readOnly: true }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ width: '12%' }}>
                                      {isEditable ? (
                                        <TextField
                                          size="small"
                                          fullWidth
                                          label={t('fields.quantityDays')}
                                          type="number"
                                          value={row.quantity || 1}
                                          onChange={(e) => {
                                            const newQuantity = parseInt(e.target.value) || 1;
                                            handleServiceRowChange(index, 'quantity', newQuantity);
                                            // If Rental service, update description when quantity changes
                                            if (row.serviceType === 'RENTAL' && selectedVehicle && startDate && (endDate || returnDateUnknown)) {
                                              const licensePlate = (selectedVehicle as any).licensePlate || t('common.unavailable', 'N/A');
                                              const newDescription = `${licensePlate} - ${selectedVehicle.make} ${selectedVehicle.model}`;
                                              handleServiceRowChange(index, 'description', newDescription);
                                            }
                                          }}
                                          inputProps={{ min: 1 }}
                                          sx={{ 
                                            '& .MuiInputBase-root': { height: '40px' },
                                            '& .MuiInputBase-input': { py: 0.75, textAlign: 'center' }
                                          }}
                                        />
                                      ) : (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={t('fields.quantityDays')}
                                          value={isPendingRental ? '' : (row.quantity || 1)}
                                          InputProps={{ readOnly: true }}
                                          sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ width: '14%' }}>
                                      {isEditable ? (
                                        <TextField
                                          size="small"
                                          fullWidth
                                          label={t('fields.unitPrice')}
                                          type="number"
                                          value={row.unitPrice || 0}
                                          onChange={isPriceEditable ? (e) => {
                                            const newPrice = parseFloat(e.target.value) || 0;
                                            handleServiceRowChange(index, 'unitPrice', newPrice);
                                          } : undefined}
                                          inputProps={{ min: 0, step: 1 }}
                                          InputProps={{
                                            readOnly: !isPriceEditable,
                                            startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>,
                                          }}
                                          sx={{ 
                                            '& .MuiInputBase-root': { height: '40px' },
                                            '& .MuiInputBase-input': { py: 0.75 }
                                          }}
                                        />
                                      ) : (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={t('fields.unitPrice')}
                                          value={isPendingRental ? '' : formatCurrency(row.unitPrice)}
                                          InputProps={{ readOnly: true }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ width: '14%' }}>
                                      {isEditable ? (
                                        <TextField
                                          size="small"
                                          fullWidth
                                          label={t('fields.total')}
                                          type="number"
                                          value={totalPrice || 0}
                                          onChange={isPriceEditable ? (e) => {
                                            const newTotal = parseFloat(e.target.value) || 0;
                                            const quantity = row.quantity || 1;
                                            // Recalculate unit price based on new total
                                            const newUnitPrice = quantity > 0 ? newTotal / quantity : 0;
                                            handleServiceRowChange(index, 'unitPrice', newUnitPrice);
                                          } : undefined}
                                          InputProps={{
                                            readOnly: !isPriceEditable,
                                            startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment>,
                                          }}
                                          sx={{ 
                                            '& .MuiInputBase-root': { height: '40px' },
                                            '& .MuiInputBase-input': { py: 0.75 }
                                          }}
                                        />
                                      ) : (
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label={t('fields.total')}
                                          value={isPendingRental ? '' : formatCurrency(totalPrice)}
                                          InputProps={{ readOnly: true }}
                                          sx={{ '& .MuiInputBase-input': { fontWeight: 'bold' } }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: '10%' }}>
                                      {row.serviceType === 'RENTAL' ? (
                                        <Tooltip title={t('rental.addService')}>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={handleAddServiceRow}
                                            aria-label={t('rental.addService')}
                                            disabled={isPendingRental}
                                            sx={{
                                              minWidth: '32px',
                                              minHeight: '32px',
                                              border: '1px solid',
                                              borderColor: 'divider',
                                            }}
                                          >
                                            <AddIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title={t('common.delete')}>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteServiceRow(index)}
                                            sx={{ minWidth: '32px', minHeight: '32px' }}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )}
                </Box>
              )}

              {SHOW_RENTAL_DEFECTS_SECTION && (
                <Box sx={{ width: '100%', mt: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {t('fields.defectsDamages')}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddDefectIcon />}
                          onClick={handleAddDefect}
                        >
                          {t('rental.addDefect')}
                        </Button>
                      </Box>
                      {defects.length === 0 ? (
                        <Alert severity="info" sx={{ py: 0 }}>{t('rental.noDefects')}</Alert>
                      ) : (
                        <List>
                          {defects.map((defect, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                              }}
                              secondaryAction={
                                <IconButton edge="end" onClick={() => handleDeleteDefect(index)}>
                                  <DeleteDefectIcon />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2">{defect.title}</Typography>
                                    <Chip label={defectTypeLabel(t, defect.type)} size="small" />
                                    <Chip
                                      label={defectSeverityLabel(t, defect.severity)}
                                      size="small"
                                      color={
                                        defect.severity === DefectSeverity.CRITICAL ? 'error' :
                                        defect.severity === DefectSeverity.HIGH ? 'warning' : 'default'
                                      }
                                    />
                                  </Box>
                                }
                                secondary={defect.description}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                  </Box>
              )}

              </TabPanel>

              {selectedReservation && (
                <TabPanel value={detailTab} index={1}>
                  {loadingSelectedReservationInvoice ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : !selectedReservationInvoice ? (
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: 'wrap',
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="h6">{t('invoices.details')}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedReservation.reservationNumber}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={
                            createReservationInvoiceMutation.isLoading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <ReceiptLongIcon fontSize="small" />
                            )
                          }
                          onClick={handleCreateOrOpenInvoice}
                          disabled={createReservationInvoiceMutation.isLoading}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('rental.invoice.createButton', { defaultValue: 'Create invoice' })}
                        </Button>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('rental.invoice.emptyState', {
                          defaultValue: 'No invoice exists for this rental yet. Create it here to print or email it directly from the rental.',
                        })}
                      </Alert>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {t('fields.subtotal')}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {formatCurrency(getReservationSubtotal(selectedReservation))}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {t('fields.totalAmount')}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {formatCurrency(selectedReservation.totalAmount)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {t('rental.depositReceived', 'Deposit received')}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {formatCurrency(selectedReservation.depositPaid)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>
                  ) : (
                    <InvoiceDetailsPanel
                      invoice={selectedReservationInvoice}
                      metaControl={
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={Boolean(isSelectedReservationInvoicePaid)}
                              onChange={handleInvoicePaidChange}
                              disabled={
                                Boolean(isSelectedReservationInvoicePaid) ||
                                markReservationInvoicePaidMutation.isLoading
                              }
                            />
                          }
                          label={t('common.paid', { defaultValue: 'Paid' })}
                        />
                      }
                    />
                  )}
                </TabPanel>
              )}

              {selectedReservation && (
                <TabPanel value={detailTab} index={2}>
                  {loadingReservationHistory ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : reservationHistory.length === 0 ? (
                    <Alert severity="info">{t('rental.noHistory')}</Alert>
                  ) : (
                    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {reservationHistory.map((event, index) => (
                        <ListItem
                          key={event.id}
                          alignItems="flex-start"
                          sx={{ px: 0, gap: 1.5 }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              minWidth: 16,
                              position: 'relative',
                              display: 'flex',
                              justifyContent: 'center',
                              flexShrink: 0,
                              alignSelf: 'stretch',
                            }}
                          >
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                                mt: 1.5,
                                zIndex: 1,
                              }}
                            />
                            {index < reservationHistory.length - 1 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 22,
                                  bottom: -12,
                                  width: 2,
                                  bgcolor: 'divider',
                                }}
                              />
                            )}
                          </Box>
                          <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {describeReservationHistoryEvent(event)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatReservationHistoryTimestamp(event.changedAt)} - {event.performedByDisplayName || t('common.unknown')}
                            </Typography>
                            {event.reason ? (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {event.reason}
                              </Typography>
                            ) : null}
                          </Paper>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </TabPanel>
              )}

              {selectedReservation && (
                <TabPanel value={detailTab} index={3}>
                  <EmailLogPanel documentType="reservation" documentId={selectedReservation.id.toString()} />
                </TabPanel>
              )}

            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Rental list filter dialog: choose the status-based list mode and, for Upcoming/All,
          optionally widen the default date window. */}
      <Dialog
        open={rentalFilterDialogOpen}
        onClose={() => setRentalFilterDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('common.filter', { defaultValue: 'Filter' })}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('rental.list.filterHint', {
              defaultValue: "Only applies to Upcoming and All - Active always shows every rental in progress.",
            })}
          </Typography>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('rental.list.modeLabel', { defaultValue: 'View' })}
              </Typography>
              <ToggleButtonGroup
                value={rentalFilterDraftMode}
                exclusive
                onChange={(_event, newFilter) => {
                  if (newFilter !== null) {
                    setRentalFilterDraftMode(newFilter);
                  }
                }}
                size="small"
                sx={{
                  width: '100%',
                  '& .MuiToggleButtonGroup-grouped': {
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    py: 0.75,
                  },
                }}
              >
                <ToggleButton value="active">{t('common.active')}</ToggleButton>
                <ToggleButton value="upcoming">
                  {t('rental.list.upcoming', { defaultValue: 'Upcoming' })}
                </ToggleButton>
                <ToggleButton value="all">{t('common.all')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel id="rental-list-status-filter-label">
                {t('fields.status', { defaultValue: 'Status' })}
              </InputLabel>
              <Select
                labelId="rental-list-status-filter-label"
                value={rentalFilterDraftStatus}
                label={t('fields.status', { defaultValue: 'Status' })}
                onChange={(event) =>
                  setRentalFilterDraftStatus(event.target.value as RentalListStatusFilter)
                }
              >
                <MenuItem value={RENTAL_LIST_STATUS_FILTER_ALL}>
                  {t('common.all')}
                </MenuItem>
                {Object.values(ReservationStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {statusLabel(t, status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('rental.list.dateRangeTitle', { defaultValue: 'Date range' })}
              </Typography>
            </Box>
            <DateInputField
              label={t('rental.list.dateRangeFieldLabelStart', { defaultValue: 'From' })}
              value={rentalFilterDraftStart}
              onChange={setRentalFilterDraftStart}
              fullWidth
              disabled={!rentalListDateRangeEnabled}
            />
            <DateInputField
              label={t('rental.list.dateRangeFieldLabelEnd', { defaultValue: 'To' })}
              value={rentalFilterDraftEnd}
              onChange={setRentalFilterDraftEnd}
              fullWidth
              disabled={!rentalListDateRangeEnabled}
              minDate={rentalFilterDraftStart || undefined}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleResetRentalListFilter}>
            {t('rental.list.resetFilter', { defaultValue: 'Reset' })}
          </Button>
          <Button
            variant="contained"
            disabled={!canApplyRentalListFilter}
            onClick={handleApplyRentalListFilter}
          >
            {t('rental.list.applyFilter', { defaultValue: 'Apply' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Driver Dialog */}
      <Dialog
        open={showNewDriverDialog}
        onClose={() => {
          setShowNewDriverDialog(false);
          setNewCustomerData(createEmptyRentalDriverForm());
          resetNewCustomerLicenseUpload();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('rental.registerNewDriver', 'New Driver')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Alert severity="info">
              {t(
                'booking.document.helper',
                'Upload the driver license to auto-fill the form. We will never submit the booking automatically, and the driver can review everything before confirming.'
              )}
            </Alert>

            <input
              ref={newCustomerFrontLicenseInputRef}
              hidden
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => handleNewCustomerLicenseFileSelected('front', event.target.files?.[0] || null)}
            />
            <input
              ref={newCustomerBackLicenseInputRef}
              hidden
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => handleNewCustomerLicenseFileSelected('back', event.target.files?.[0] || null)}
            />

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  onClick={() => newCustomerFrontLicenseInputRef.current?.click()}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setNewCustomerLicenseDraggingSide('front');
                  }}
                  onDragLeave={() => setNewCustomerLicenseDraggingSide((current) => (current === 'front' ? null : current))}
                  onDrop={(event) => handleNewCustomerLicenseDrop('front', event)}
                  sx={{
                    p: 1.5,
                    minHeight: 150,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: newCustomerLicenseDraggingSide === 'front' ? 'primary.main' : 'divider',
                    bgcolor: newCustomerLicenseDraggingSide === 'front' ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {newCustomerFrontLicensePreviewUrl ? (
                    <Box sx={{ position: 'relative', height: '100%' }}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRemoveNewCustomerLicenseFile('front');
                        }}
                        sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, bgcolor: 'rgba(255,255,255,0.92)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Box
                        component="img"
                        src={newCustomerFrontLicensePreviewUrl}
                        alt={t('booking.document.frontPreview', 'Front preview')}
                        sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {newCustomerFrontLicenseFile?.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0.75} alignItems="center" justifyContent="center" sx={{ height: '100%', textAlign: 'center' }}>
                      <CloudUpload color="primary" sx={{ fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700}>
                        {t('booking.document.frontUpload', 'Upload front side')}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  onClick={() => newCustomerBackLicenseInputRef.current?.click()}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setNewCustomerLicenseDraggingSide('back');
                  }}
                  onDragLeave={() => setNewCustomerLicenseDraggingSide((current) => (current === 'back' ? null : current))}
                  onDrop={(event) => handleNewCustomerLicenseDrop('back', event)}
                  sx={{
                    p: 1.5,
                    minHeight: 150,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: newCustomerLicenseDraggingSide === 'back' ? 'primary.main' : 'divider',
                    bgcolor: newCustomerLicenseDraggingSide === 'back' ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {newCustomerBackLicensePreviewUrl ? (
                    <Box sx={{ position: 'relative', height: '100%' }}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRemoveNewCustomerLicenseFile('back');
                        }}
                        sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, bgcolor: 'rgba(255,255,255,0.92)' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Box
                        component="img"
                        src={newCustomerBackLicensePreviewUrl}
                        alt={t('booking.document.backPreview', 'Back preview')}
                        sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1, mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {newCustomerBackLicenseFile?.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0.75} alignItems="center" justifyContent="center" sx={{ height: '100%', textAlign: 'center' }}>
                      <PhotoCamera color="primary" sx={{ fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700}>
                        {t('booking.document.backUpload', 'Upload back side (optional)')}
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={isNewCustomerLicenseBusy ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                onClick={handleUploadNewCustomerLicense}
                disabled={!newCustomerFrontLicenseFile || isNewCustomerLicenseBusy}
              >
                {isNewCustomerLicenseBusy
                  ? t('booking.document.processing', 'Processing document...')
                  : t('booking.document.processAction', 'Upload and auto-fill')}
              </Button>

              {newCustomerLicenseExtraction?.overallConfidence != null && (
                <Chip
                  size="small"
                  color={newCustomerLicenseProcessingState === 'completed' ? 'success' : 'default'}
                  label={t('booking.document.confidence', {
                    defaultValue: 'AI confidence: {{percent}}%',
                    percent: Math.round((newCustomerLicenseExtraction.overallConfidence || 0) * 100),
                  })}
                />
              )}
            </Stack>

            {isNewCustomerLicenseBusy && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  {newCustomerLicenseStatusMessage || t('booking.document.processing', 'Processing document...')}
                </Typography>
                <LinearProgress
                  variant={newCustomerLicenseProcessingState === 'extracting' ? 'indeterminate' : 'determinate'}
                  value={newCustomerLicenseProcessingState === 'extracting' ? undefined : newCustomerLicenseUploadProgress}
                />
              </Box>
            )}

            {newCustomerLicenseStatusMessage && !isNewCustomerLicenseBusy && (
              <Alert severity={newCustomerLicenseProcessingState === 'completed' ? 'success' : (newCustomerLicenseProcessingState === 'failed' ? 'warning' : 'info')}>
                {newCustomerLicenseStatusMessage}
              </Alert>
            )}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* Row 1: First Name | Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.firstName')}
                value={newCustomerData.firstName}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.lastName')}
                value={newCustomerData.lastName}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, lastName: e.target.value })}
                required
              />
            </Grid>
            {/* Row 2: ID Number | Date of Birth */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.personalIdNumber')}
                value={newCustomerData.personalIdNumber || ''}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, personalIdNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                fullWidth
                size="small"
                label={t('fields.dateOfBirth')}
                value={newCustomerData.dateOfBirth || ''}
                onChange={(dateOfBirth) => setNewCustomerData({ ...newCustomerData, dateOfBirth })}
                required
              />
            </Grid>
            {/* Row 3: Phone Number | Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.phoneNumber')}
                value={newCustomerData.phoneNumber}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, phoneNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.emailOptional', 'Email (optional)')}
                type="email"
                value={newCustomerData.email}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
              />
            </Grid>
            {/* Row 4: License Number | Issue Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.licenseNumber')}
                value={newCustomerData.driverLicenseNumber}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, driverLicenseNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                fullWidth
                size="small"
                label={t('fields.licenseIssueDate', 'License Issue Date')}
                value={newCustomerData.licenseIssueDate || ''}
                onChange={(licenseIssueDate) =>
                  setNewCustomerData({ ...newCustomerData, licenseIssueDate })
                }
                required
              />
            </Grid>
            {/* Row 5: Expiry Date | License Categories */}
            <Grid item xs={12} sm={6}>
              <DateInputField
                fullWidth
                size="small"
                label={t('fields.expiryDate')}
                value={newCustomerData.licenseExpiryDate || ''}
                onChange={(licenseExpiryDate) =>
                  setNewCustomerData({ ...newCustomerData, licenseExpiryDate })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.licenseCategories', 'License Categories')}
                value={newCustomerData.licenseCategories || ''}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, licenseCategories: e.target.value })}
                helperText={
                  newCustomerLicenseExtraction?.driverLicense?.entitlements?.length
                    ? newCustomerLicenseExtraction.driverLicense.entitlements
                        .map((entitlement) =>
                          [entitlement.categoryCode, entitlement.validFrom, entitlement.validTo]
                            .filter(Boolean)
                            .join(' ')
                        )
                        .join(' | ')
                    : t('booking.document.licenseCategoriesHint', 'Example: B or B, BE')
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowNewDriverDialog(false);
              setNewCustomerData(createEmptyRentalDriverForm());
              resetNewCustomerLicenseUpload();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const { firstName, lastName, personalIdNumber, dateOfBirth, phoneNumber, driverLicenseNumber, licenseIssueDate, licenseExpiryDate } = newCustomerData;
              if (!firstName?.trim() || !lastName?.trim() || !personalIdNumber?.trim() || !dateOfBirth || !phoneNumber?.trim() || !driverLicenseNumber?.trim() || !licenseIssueDate || !licenseExpiryDate) {
                toast.error(t('rental.validation.allFieldsRequired'));
                return;
              }
              createDriverMutation.mutate(newCustomerData);
            }}
            disabled={createDriverMutation.isLoading}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNewBillingPartyDialog}
        onClose={() => {
          if (createCustomerAccountMutation.isLoading) {
            return;
          }
          setShowNewBillingPartyDialog(false);
          setNewBillingPartyData(createEmptyReservationCustomerForm());
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('customers.createDialogTitle', 'New Customer')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1.5}>
            <Alert severity="info">
              {t(
                'customers.createDialogHelper',
                'Create a reusable customer for company-paid or third-party rentals.'
              )}
            </Alert>
            <CustomerFormFields
              value={newBillingPartyData}
              onChange={setNewBillingPartyData}
              disabled={createCustomerAccountMutation.isLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowNewBillingPartyDialog(false);
              setNewBillingPartyData(createEmptyReservationCustomerForm());
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={createCustomerAccountMutation.isLoading}
            onClick={() => {
              if (!newBillingPartyData.displayName.trim()) {
                toast.error(
                  t('customers.validation.displayNameRequired', 'Customer name is required.')
                );
                return;
              }
              createCustomerAccountMutation.mutate(newBillingPartyData);
            }}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver Details Dialog (view / edit an already-registered driver) */}
      <Dialog
        open={showDriverDetailsDialog}
        onClose={() => {
          setShowDriverDetailsDialog(false);
          setDriverDetailsTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{t('rental.driverDetails', 'Driver Details')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.firstName')}
                value={driverDetailsData.firstName || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.lastName')}
                value={driverDetailsData.lastName || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.personalIdNumber')}
                value={driverDetailsData.personalIdNumber || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, personalIdNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                fullWidth
                size="small"
                label={t('fields.dateOfBirth')}
                value={driverDetailsData.dateOfBirth || ''}
                onChange={(dateOfBirth) =>
                  setDriverDetailsData({ ...driverDetailsData, dateOfBirth })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.phoneNumber')}
                value={driverDetailsData.phoneNumber || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, phoneNumber: e.target.value })}
                required
                InputProps={{
                  endAdornment: getWhatsAppLink(driverDetailsData.phoneNumber) && (
                    <Tooltip title={t('rental.openWhatsApp', 'Open WhatsApp chat')}>
                      <IconButton
                        size="small"
                        component="a"
                        href={getWhatsAppLink(driverDetailsData.phoneNumber)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#25D366' }}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.emailOptional', 'Email (optional)')}
                type="email"
                value={driverDetailsData.email || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.licenseNumber')}
                value={driverDetailsData.driverLicenseNumber || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, driverLicenseNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                fullWidth
                size="small"
                label={t('fields.licenseIssueDate', 'License Issue Date')}
                value={driverDetailsData.licenseIssueDate || ''}
                onChange={(licenseIssueDate) =>
                  setDriverDetailsData({ ...driverDetailsData, licenseIssueDate })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                fullWidth
                size="small"
                label={t('fields.expiryDate')}
                value={driverDetailsData.licenseExpiryDate || ''}
                onChange={(licenseExpiryDate) =>
                  setDriverDetailsData({ ...driverDetailsData, licenseExpiryDate })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.licenseCategories', 'License Categories')}
                value={driverDetailsData.licenseCategories || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, licenseCategories: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label={t('fields.notes')}
                value={driverDetailsData.notes || ''}
                onChange={(e) => setDriverDetailsData({ ...driverDetailsData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDriverDetailsDialog(false);
              setDriverDetailsTarget(null);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!driverDetailsData.firstName?.trim() || !driverDetailsData.lastName?.trim() || !driverDetailsData.phoneNumber?.trim()) {
                toast.error(t('rental.validation.allFieldsRequired'));
                return;
              }
              updateDriverDetailsMutation.mutate(driverDetailsData);
            }}
            disabled={updateDriverDetailsMutation.isLoading}
          >
            {t('common.saveChanges', 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {SHOW_RENTAL_DEFECTS_SECTION && (
        <Dialog
        open={showDefectDialog}
        onClose={() => setShowDefectDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingDefectIndex !== null ? t('vehicleInspection.editDefect') : t('vehicleInspection.reportNewDefect')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required size="small">
                <InputLabel>{t('fields.defectType')}</InputLabel>
                <Select
                  value={defectFormData.type}
                  label={t('fields.defectType')}
                  onChange={(e) => setDefectFormData({ ...defectFormData, type: e.target.value as DefectType })}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  {Object.values(DefectType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {defectTypeLabel(t, type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required size="small">
                <InputLabel>{t('fields.severity')}</InputLabel>
                <Select
                  value={defectFormData.severity}
                  label={t('fields.severity')}
                  onChange={(e) => setDefectFormData({ ...defectFormData, severity: e.target.value as DefectSeverity })}
                  sx={{ '& .MuiSelect-select': { py: 1 } }}
                >
                  {Object.values(DefectSeverity).map((severity) => (
                    <MenuItem key={severity} value={severity}>
                      {defectSeverityLabel(t, severity)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.title')}
                value={defectFormData.title}
                onChange={(e) => setDefectFormData({ ...defectFormData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={t('fields.description')}
                multiline
                minRows={3}
                maxRows={4}
                value={defectFormData.description}
                onChange={(e) => setDefectFormData({ ...defectFormData, description: e.target.value })}
                required
                placeholder={t('placeholders.defectDescription')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('fields.location')}
                value={defectFormData.location}
                onChange={(e) => setDefectFormData({ ...defectFormData, location: e.target.value })}
                placeholder={t('placeholders.defectLocation')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('fields.component')}
                value={defectFormData.component}
                onChange={(e) => setDefectFormData({ ...defectFormData, component: e.target.value })}
                placeholder={t('placeholders.defectComponent')}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('fields.impactAssessment')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('fields.safetyImpact')}</InputLabel>
                <Select
                  value={defectFormData.safetyImpact ? 'yes' : 'no'}
                  label={t('fields.safetyImpact')}
                  onChange={(e) => setDefectFormData({ ...defectFormData, safetyImpact: e.target.value === 'yes' })}
                >
                  <MenuItem value="no">{t('common.no')}</MenuItem>
                  <MenuItem value="yes">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t('fields.vehicleOutOfService')}</InputLabel>
                <Select
                  value={defectFormData.vehicleOutOfService ? 'yes' : 'no'}
                  label={t('fields.vehicleOutOfService')}
                  onChange={(e) => setDefectFormData({ ...defectFormData, vehicleOutOfService: e.target.value === 'yes' })}
                >
                  <MenuItem value="no">{t('common.no')}</MenuItem>
                  <MenuItem value="yes">{t('common.yes')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDefectDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSaveDefect}>
            {editingDefectIndex !== null ? t('common.updateItem', { item: t('fields.defectType') }) : t('common.addItem', { item: t('fields.defectType') })}
          </Button>
        </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={openDocumentsDialog}
        onClose={handleCloseDocumentsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('rental.documents.title', {
            number: selectedReservation?.reservationNumber || '',
            defaultValue: `Rental documents${selectedReservation?.reservationNumber ? ` - ${selectedReservation.reservationNumber}` : ''}`,
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: isDraggingDocuments ? 'primary.main' : 'divider',
                bgcolor: isDraggingDocuments ? 'rgba(25, 118, 210, 0.05)' : 'background.paper',
                transition: 'all 0.2s ease',
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingDocuments(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDraggingDocuments(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDraggingDocuments(false);
                const files = Array.from(event.dataTransfer.files || []);
                if (files.length > 0) {
                  setSelectedDocumentFiles(files);
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  size="small"
                  disabled={uploadDocumentsMutation.isLoading}
                  sx={{ textTransform: 'none' }}
                >
                  {t('rental.documents.choose', { defaultValue: 'Choose document' })}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept={DOCUMENT_ACCEPT}
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []);
                      event.target.value = '';
                      setSelectedDocumentFiles(files);
                    }}
                  />
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUploadDocuments}
                  disabled={selectedDocumentFiles.length === 0 || uploadDocumentsMutation.isLoading || !selectedReservation}
                  sx={{ textTransform: 'none' }}
                >
                  {uploadDocumentsMutation.isLoading
                    ? t('common.uploading')
                    : t('rental.documents.upload', { defaultValue: 'Upload' })}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {selectedDocumentFiles.length === 0
                    ? t('rental.documents.dragHint', { defaultValue: 'or drag and drop files here' })
                    : selectedDocumentFiles.length === 1
                      ? selectedDocumentFiles[0].name
                      : t('rental.documents.selectionCount', { count: selectedDocumentFiles.length, defaultValue: `${selectedDocumentFiles.length} documents selected` })}
                </Typography>
              </Box>
            </Paper>

            {uploadDocumentsMutation.isLoading && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  {t('rental.documents.uploading', { defaultValue: 'Uploading documents securely...' })}
                </Typography>
                <LinearProgress
                  variant={documentUploadProgress > 0 ? 'determinate' : 'indeterminate'}
                  value={documentUploadProgress || undefined}
                />
              </Box>
            )}

            {!isLoadingDriverLicenseDocuments && reusableDriverLicenseDocuments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('rental.documents.driverLicenseOnFile', { defaultValue: "Driver's license on file" })}
                </Typography>
                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {reusableDriverLicenseDocuments.map((managedDocument) => {
                    const primaryFile = managedDocument.files[0];
                    return (
                      <ListItem
                        key={`driver-license-${managedDocument.id}`}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          alignItems: 'flex-start',
                          bgcolor: 'action.hover',
                        }}
                        secondaryAction={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={t('common.open')}>
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewDocument(managedDocument)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.download')}>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(managedDocument)}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 9 }}>
                              <AssignmentIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                {managedDocument.displayName || primaryFile?.originalFileName}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pr: 9, mt: 0.5 }}>
                              {[
                                t('rental.documents.driverLicenseOnFileHint', { defaultValue: 'Previously uploaded for this driver' }),
                                formatReservationHistoryTimestamp(managedDocument.createdAt),
                              ].filter(Boolean).join(' • ')}
                            </Typography>
                          }
                          sx={{ my: 0 }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            {isLoadingReservationDocuments ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : reservationDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('rental.documents.empty', { defaultValue: 'No documents uploaded for this rental yet.' })}
              </Typography>
            ) : (
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reservationDocuments.map((managedDocument) => {
                  const primaryFile = managedDocument.files[0];
                  return (
                    <ListItem
                      key={managedDocument.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 1,
                        alignItems: 'flex-start',
                      }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={t('common.open')}>
                            <IconButton
                              size="small"
                              onClick={() => handlePreviewDocument(managedDocument)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.download')}>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadDocument(managedDocument)}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDocument(managedDocument)}
                              disabled={deleteDocumentMutation.isLoading}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 13 }}>
                            <AssignmentIcon fontSize="small" color="action" />
                            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                              {managedDocument.displayName || primaryFile?.originalFileName}
                            </Typography>
                            <Chip
                              label={managedDocument.documentTypeLabel}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ pr: 13, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {[
                                primaryFile?.originalFileName,
                                primaryFile ? formatDocumentFileSize(primaryFile.fileSizeBytes) : null,
                                formatReservationHistoryTimestamp(managedDocument.createdAt),
                                managedDocument.uploadedByDisplayName || t('common.unknown'),
                              ].filter(Boolean).join(' • ')}
                            </Typography>
                            {managedDocument.description ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {managedDocument.description}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                        sx={{ my: 0 }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocumentsDialog}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
          setPendingAction(null);
        }}
      >
        <DialogTitle>{t('dialogs.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('rental.unsavedSaveBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowUnsavedDialog(false);
            setPendingAction(null);
          }}>{t('common.cancel')}</Button>
          <Button onClick={() => {
            setShowUnsavedDialog(false);
            setPendingAction(null);
            setIsDirty(false);
            if (pendingAction) {
              pendingAction();
            }
          }}>{t('common.discardChanges')}</Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              await handleSave();
              if (!createReservationMutation.isLoading && !updateReservationMutation.isLoading) {
                setShowUnsavedDialog(false);
                if (pendingAction) {
                  pendingAction();
                }
                setPendingAction(null);
              }
            }}
          >
            {t('common.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mileage Warning Dialog */}
      <Dialog
        open={showMileageWarningDialog}
        onClose={handleMileageWarningCancel}
      >
        <DialogTitle>{t('rental.mileageWarning')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line', mb: 2 }}>
            {mileageWarningMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common.continueQuestion')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMileageWarningCancel}>{t('common.no')}</Button>
          <Button 
            variant="contained" 
            onClick={handleMileageWarningConfirm}
            color="primary"
          >{t('common.yes')}</Button>
        </DialogActions>
      </Dialog>

      {selectedReservation && (
        <EmailSendDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          documentType="reservation"
          documentId={selectedReservation.id.toString()}
          documentLabel={selectedReservation.reservationNumber}
        />
      )}

      {selectedReservation && (
        <WhatsAppSendDialog
          open={whatsappDialogOpen}
          onClose={() => setWhatsappDialogOpen(false)}
          documentType="reservation"
          documentId={selectedReservation.id.toString()}
          documentLabel={selectedReservation.reservationNumber}
        />
      )}
      </Box>
      </Box>
    </Container>
    </LocalizationProvider>
  );
};
